import { DiffModeEnum, DiffView, SplitSide } from "@git-diff-view/react";
import "@git-diff-view/react/styles/diff-view-pure.css";
import { common, createLowlight } from "lowlight";
import {
	AlertCircle,
	ArrowDown,
	ArrowUp,
	Bold,
	BookOpenText,
	Braces,
	CheckCircle2,
	ChevronDown,
	ChevronRight,
	ChevronsDown,
	ChevronsUp,
	Clock,
	Code,
	CodeXml,
	Copy,
	CornerDownRight,
	Database,
	Edit3,
	ExternalLink,
	Eye,
	EyeOff,
	File,
	FileArchive,
	FileAudio,
	FileCode,
	FileDiff,
	FileDown,
	FileImage,
	FileJson,
	FileKey,
	FileLock,
	FileSliders,
	FileSpreadsheet,
	FileTerminal,
	FileText,
	FileUp,
	FileVideo,
	Folder,
	FolderOpen,
	Globe,
	Italic,
	Link,
	List,
	Loader2,
	LocateFixed,
	MessageSquare,
	MessagesSquare,
	Package,
	Palette,
	PanelLeftClose,
	PanelLeftOpen,
	Quote,
	RefreshCcw,
	Reply,
	Save,
	Search,
	Send,
	ThumbsDown,
	ThumbsUp,
	Trash2,
	WrapText,
	X,
} from "lucide-react";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { THEMES, useTheme } from "../ThemeContext";
import {
	DIFF_VIEWER_PRESENTATION,
	DIFF_VIEWER_PRESENTATION_SELECTED_STORAGE_KEY,
	DIFF_VIEWER_PRESENTATION_STORAGE_KEY,
	GITLAB_BASE_URL,
} from "../constants";
import { fuzzyMatchText, normalizeSearchQuery } from "../fuzzySearch";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useMergeRequestDiff } from "../hooks/useMergeRequestDiff";
import {
	copyTextToClipboard,
	formatProjectName,
	safeHttpUrl,
} from "../utils";
import { Avatar } from "./Avatar";
import { DateTime } from "./DateTime";
import {
	CommentBody,
	EmojiReactionBar,
	MRCommentsThreadedPanel,
	useMRComments,
} from "./MRComments";
import { Tooltip } from "./Tooltip";

const VIEW_MODE = Object.freeze({
	SPLIT: "split",
	UNIFIED: "unified",
});

const PRESENTATION_MODE = DIFF_VIEWER_PRESENTATION;

const DIFF_FILE_QUERY_PARAM = "file";
const DIFF_EAGER_FILE_COUNT = 4;
const DIFF_LAZY_ROOT_MARGIN = "700px 0px 900px 0px";
const COMMENT_THREAD_SELECTOR = "[data-diff-comment-thread='true']";
const COMMENT_CODE_CONTEXT_LINES = 1;
const LINE_JUMP_HIGHLIGHT_MS = 1600;
const codeHighlighter = createLowlight(common);
const DIFF_COMMENTS_PANEL_DEFAULT_HEIGHT = 360;
const DIFF_COMMENTS_PANEL_MIN_HEIGHT = 180;
const DIFF_COMMENTS_PANEL_MAX_HEIGHT = 720;
const DIFF_SYNTAX_THEME_STORAGE_KEY = "gitlab-diff-view-syntax-theme";
const DIFF_SYNTAX_THEME_AUTO = "auto";
const DIFF_SYNTAX_THEME_OPTIONS = Object.freeze([
	{ value: DIFF_SYNTAX_THEME_AUTO, label: "Auto", diffTheme: null },
	{ value: "github-light", label: "GitHub Light", diffTheme: "light" },
	{ value: "github-dark", label: "GitHub Dark", diffTheme: "dark" },
	{ value: "dracula", label: "Dracula", diffTheme: "dark" },
	{ value: "monokai", label: "Monokai", diffTheme: "dark" },
	{ value: "nord", label: "Nord", diffTheme: "dark" },
	{ value: "one-dark", label: "One Dark", diffTheme: "dark" },
	{ value: "solarized-light", label: "Solarized Light", diffTheme: "light" },
	{ value: "solarized-dark", label: "Solarized Dark", diffTheme: "dark" },
	{ value: "gruvbox-dark", label: "Gruvbox Dark", diffTheme: "dark" },
	{ value: "ayu-dark", label: "Ayu Dark", diffTheme: "dark" },
]);

const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

const getDiffSyntaxThemeOption = (value) =>
	DIFF_SYNTAX_THEME_OPTIONS.find((option) => option.value === value) ||
	DIFF_SYNTAX_THEME_OPTIONS[0];

const readActiveFilePathFromLocation = () => {
	if (typeof window === "undefined") return "";
	return (
		new URLSearchParams(window.location.search).get(DIFF_FILE_QUERY_PARAM) || ""
	);
};

const writeActiveFilePathToHistory = (filePath) => {
	if (typeof window === "undefined") return;

	const url = new URL(window.location.href);
	if (filePath) {
		url.searchParams.set(DIFF_FILE_QUERY_PARAM, filePath);
	} else {
		url.searchParams.delete(DIFF_FILE_QUERY_PARAM);
	}

	window.history.replaceState(
		{
			...(window.history.state || {}),
			dashboardDiffFile: filePath || null,
		},
		"",
		`${url.pathname}${url.search}${url.hash}`,
	);
};

const fileElementId = (file) =>
	`mr-diff-file-${file.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

const findFileByPath = (files, filePath) => {
	const normalizedPath = String(filePath || "").trim();
	if (!normalizedPath) return null;

	return (
		files.find(
			(file) =>
				file.path === normalizedPath ||
				file.newPath === normalizedPath ||
				file.oldPath === normalizedPath,
		) || null
	);
};

const getAncestorDirPaths = (filePath) => {
	const parts = String(filePath || "")
		.split("/")
		.filter(Boolean);

	return parts
		.slice(0, -1)
		.map((_, index) => parts.slice(0, index + 1).join("/"));
};

const encodePathSegments = (path) =>
	String(path || "")
		.split("/")
		.map((segment) => encodeURIComponent(segment))
		.join("/");

const buildGitLabFileUrl = ({ projectPath, diffRefs, file }) => {
	const commitSha = file?.deletedFile ? diffRefs?.startSha : diffRefs?.headSha;
	const filePath = file?.deletedFile ? file?.oldPath : file?.newPath;
	if (!projectPath || !commitSha || !filePath) return "";

	return safeHttpUrl(
		`${GITLAB_BASE_URL}/${encodePathSegments(projectPath)}/-/blob/${encodeURIComponent(commitSha)}/${encodePathSegments(filePath)}`,
	);
};

const buildGitLabTreeUrl = ({ projectPath, diffRefs, path }) => {
	const commitSha = diffRefs?.headSha;
	if (!projectPath || !commitSha || !path) return "";

	return safeHttpUrl(
		`${GITLAB_BASE_URL}/${encodePathSegments(projectPath)}/-/tree/${encodeURIComponent(commitSha)}/${encodePathSegments(path)}`,
	);
};

const buildGitLabPathUrl = ({ projectPath, diffRefs, menu }) => {
	if (!menu) return "";
	if (menu.file) {
		return buildGitLabFileUrl({ projectPath, diffRefs, file: menu.file });
	}
	return buildGitLabTreeUrl({ projectPath, diffRefs, path: menu.path });
};

const buildGitLabLineUrl = ({
	projectPath,
	diffRefs,
	file,
	lineNumber,
	startLine,
	endLine,
	side,
}) => {
	const commitSha =
		side === SplitSide.old ? diffRefs?.startSha : diffRefs?.headSha;
	const filePath = side === SplitSide.old ? file?.oldPath : file?.newPath;
	const firstLine = Number(startLine ?? lineNumber);
	const lastLine = Number(endLine ?? firstLine);
	const hasRange =
		Number.isFinite(firstLine) &&
		firstLine > 0 &&
		Number.isFinite(lastLine) &&
		lastLine > 0;

	if (!projectPath || !commitSha || !filePath || !hasRange) return "";

	const rangeStart = Math.min(firstLine, lastLine);
	const rangeEnd = Math.max(firstLine, lastLine);
	const lineHash =
		rangeStart === rangeEnd ? `#L${rangeStart}` : `#L${rangeStart}-${rangeEnd}`;

	return safeHttpUrl(
		`${GITLAB_BASE_URL}/${encodePathSegments(projectPath)}/-/blob/${encodeURIComponent(commitSha)}/${encodePathSegments(filePath)}${lineHash}`,
	);
};

const copyText = async (text) => {
	if (!text) return false;
	try {
		return await copyTextToClipboard(text);
	} catch (error) {
		console.error("Failed to copy diff text:", error);
		return false;
	}
};

const readPositiveLineNumber = (value) => {
	const lineNumber = Number(value);
	return Number.isFinite(lineNumber) && lineNumber > 0 ? lineNumber : null;
};

const splitSideFromString = (value) =>
	String(value || "").toLowerCase() === "old" ? SplitSide.old : SplitSide.new;

const isEditableKeyboardTarget = (target) =>
	target instanceof HTMLElement &&
	(target.isContentEditable ||
		["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName));

const consumeEscapeKey = (event) => {
	event.preventDefault();
	event.stopPropagation();
	event.nativeEvent?.stopImmediatePropagation?.();
	event.stopImmediatePropagation?.();
};

const isApplePlatform = () =>
	typeof navigator !== "undefined" &&
	/Mac|iPhone|iPad|iPod/.test(navigator.platform || "");

const getDiffCommentsShortcut = () =>
	isApplePlatform() ? "⌘+⌥+C" : "Ctrl+Alt+C";

const getDiffCommentsAriaShortcut = () =>
	isApplePlatform() ? "Meta+Alt+C" : "Control+Alt+C";

const matchesShortcutKey = (event, key) => {
	const normalizedKey = String(key || "").toLowerCase();
	return (
		String(event.key || "").toLowerCase() === normalizedKey ||
		String(event.code || "").toLowerCase() === `key${normalizedKey}`
	);
};

const isDiffCommentsShortcutEvent = (event) =>
	(event.metaKey || event.ctrlKey) &&
	event.altKey &&
	!event.shiftKey &&
	matchesShortcutKey(event, "c");

const selectedRangeBounds = (selection) => {
	if (!selection) return null;
	const start = Math.min(selection.startLine, selection.endLine);
	const end = Math.max(selection.startLine, selection.endLine);
	return { start, end };
};

const selectionContainsLine = (selection, file, { lineNumber, side }) => {
	const bounds = selectedRangeBounds(selection);
	return (
		Boolean(bounds) &&
		selection.fileId === file?.id &&
		selection.side === side &&
		lineNumber >= bounds.start &&
		lineNumber <= bounds.end
	);
};

const lineSelectionFromContext = (file, { lineNumber, side }) => ({
	fileId: file.id,
	side,
	anchorLine: lineNumber,
	startLine: lineNumber,
	endLine: lineNumber,
});

const fileStatus = (file) => {
	if (file.renamedFile) {
		return {
			label: "R",
			className: "text-blue-500",
			badgeClassName: "border-blue-500/40 bg-blue-500/10 text-blue-500",
		};
	}
	if (file.newFile) {
		return {
			label: "A",
			className: "text-green-500",
			badgeClassName: "border-green-500/40 bg-green-500/10 text-green-500",
		};
	}
	if (file.deletedFile) {
		return {
			label: "D",
			className: "text-red-500",
			badgeClassName: "border-red-500/40 bg-red-500/10 text-red-500",
		};
	}
	return {
		label: "M",
		className: "text-amber-500",
		badgeClassName: "border-amber-500/40 bg-amber-500/10 text-amber-500",
	};
};

const FILE_ICON_BY_NAME = {
	".dockerignore": FileSliders,
	".env": FileKey,
	".env.example": FileKey,
	".gitattributes": FileSliders,
	".gitignore": FileSliders,
	".gitlab-ci.yml": FileSliders,
	".npmrc": FileSliders,
	".prettierrc": FileSliders,
	".prettierrc.json": FileJson,
	"biome.json": FileSliders,
	"cargo.lock": FileLock,
	"composer.lock": FileLock,
	"docker-compose.yaml": Package,
	"docker-compose.yml": Package,
	dockerfile: Package,
	gemfile: Package,
	gemfile_lock: FileLock,
	license: FileText,
	makefile: FileTerminal,
	package: Package,
	"package-lock.json": FileLock,
	"package.json": Package,
	pnpm_lock: FileLock,
	procfile: FileTerminal,
	readme: BookOpenText,
	"requirements.txt": Package,
	tsconfig: FileSliders,
	"tsconfig.json": FileSliders,
	yarn_lock: FileLock,
};

const FILE_ICON_BY_EXTENSION = {
	ai: FileImage,
	apng: FileImage,
	avi: FileVideo,
	avif: FileImage,
	babelrc: FileSliders,
	bash: FileTerminal,
	bmp: FileImage,
	bzl: FileCode,
	c: FileCode,
	cer: FileKey,
	cfg: FileSliders,
	conf: FileSliders,
	config: FileSliders,
	cpp: FileCode,
	crt: FileKey,
	cs: FileCode,
	css: Palette,
	csv: FileSpreadsheet,
	dart: FileCode,
	db: Database,
	dockerfile: Package,
	doc: FileText,
	docx: FileText,
	env: FileKey,
	ex: FileCode,
	exs: FileCode,
	fig: FileImage,
	gif: FileImage,
	go: FileCode,
	gql: Braces,
	graphql: Braces,
	gz: FileArchive,
	h: FileCode,
	heic: FileImage,
	heif: FileImage,
	htm: CodeXml,
	html: CodeXml,
	ico: FileImage,
	ini: FileSliders,
	java: FileCode,
	jpeg: FileImage,
	jpg: FileImage,
	js: FileCode,
	json: FileJson,
	jsonc: FileJson,
	jsx: CodeXml,
	key: FileKey,
	lock: FileLock,
	log: FileText,
	lua: FileCode,
	md: BookOpenText,
	mdx: BookOpenText,
	mjs: FileCode,
	mov: FileVideo,
	mp3: FileAudio,
	mp4: FileVideo,
	ogg: FileAudio,
	pdf: FileText,
	pem: FileKey,
	php: FileCode,
	pl: FileCode,
	png: FileImage,
	proto: FileCode,
	psd: FileImage,
	py: FileCode,
	rb: FileCode,
	rs: FileCode,
	rss: Globe,
	sass: Palette,
	scss: Palette,
	sh: FileTerminal,
	sql: Database,
	sqlite: Database,
	svg: FileImage,
	swift: FileCode,
	tar: FileArchive,
	tf: FileSliders,
	toml: FileSliders,
	ts: FileCode,
	tsx: CodeXml,
	txt: FileText,
	vue: CodeXml,
	wav: FileAudio,
	webm: FileVideo,
	webp: FileImage,
	xml: CodeXml,
	yaml: FileSliders,
	yml: FileSliders,
	zip: FileArchive,
};

const normalizeFileIconKey = (value) =>
	String(value || "")
		.trim()
		.toLowerCase()
		.replace(/[.-]/g, "_");

const getFileIconComponent = (path) => {
	const name = fileName(String(path || ""));
	const lowerName = name.toLowerCase();
	const nameWithoutExtension = lowerName.split(".")[0];
	const extension = lowerName.includes(".") ? lowerName.split(".").pop() : "";
	const normalizedName = normalizeFileIconKey(lowerName);
	const normalizedBaseName = normalizeFileIconKey(nameWithoutExtension);

	return (
		FILE_ICON_BY_NAME[lowerName] ||
		FILE_ICON_BY_NAME[normalizedName] ||
		FILE_ICON_BY_NAME[normalizedBaseName] ||
		FILE_ICON_BY_EXTENSION[normalizeFileIconKey(extension)] ||
		File
	);
};

const FileKindIcon = ({ path, className }) => {
	const Icon = getFileIconComponent(path);
	return <Icon aria-hidden="true" className={className} strokeWidth={1.8} />;
};

const buildFileTree = (files) => {
	const root = { type: "dir", name: "", path: "", children: new Map() };

	for (const file of files) {
		const parts = file.path.split("/").filter(Boolean);
		let current = root;

		parts.forEach((part, index) => {
			const path = parts.slice(0, index + 1).join("/");
			const isFile = index === parts.length - 1;

			if (isFile) {
				current.children.set(path, {
					type: "file",
					name: part,
					path,
					file,
				});
				return;
			}

			if (!current.children.has(path)) {
				current.children.set(path, {
					type: "dir",
					name: part,
					path,
					children: new Map(),
				});
			}
			current = current.children.get(path);
		});
	}

	const sortNode = (node) => {
		if (node.type !== "dir") return node;

		return {
			...node,
			children: [...node.children.values()].map(sortNode).sort((a, b) => {
				if (a.type !== b.type) return a.type === "dir" ? -1 : 1;
				return a.name.localeCompare(b.name);
			}),
		};
	};

	return sortNode(root);
};

const collectDirPaths = (node, paths = []) => {
	if (node.type !== "dir") return paths;
	if (node.path) paths.push(node.path);
	for (const child of node.children || []) collectDirPaths(child, paths);
	return paths;
};

const fileName = (path) => path.split("/").pop() || path;

const fileMatchesQuery = (file, query) => {
	const normalizedQuery = normalizeSearchQuery(query).toLowerCase();
	if (!normalizedQuery) return true;
	return fuzzyMatchText(file.path, normalizedQuery).matched;
};

const filterFileTree = (node, query) => {
	const normalizedQuery = normalizeSearchQuery(query).toLowerCase();
	if (!normalizedQuery) return node;

	if (node.type === "file") {
		return fileMatchesQuery(node.file, normalizedQuery) ? node : null;
	}

	const dirMatches =
		node.path && fuzzyMatchText(node.path, normalizedQuery).matched;
	const children = dirMatches
		? node.children || []
		: (node.children || [])
				.map((child) => filterFileTree(child, normalizedQuery))
				.filter(Boolean);

	if (node.path && !dirMatches && children.length === 0) return null;
	return { ...node, children };
};

const countFilesInTree = (node) => {
	if (!node) return 0;
	if (node.type === "file") return 1;
	return (node.children || []).reduce(
		(count, child) => count + countFilesInTree(child),
		0,
	);
};

const scoreFileMatch = (file, query) => {
	const normalizedQuery = normalizeSearchQuery(query).toLowerCase();
	if (!normalizedQuery) return 0;

	const path = file.path.toLowerCase();
	const name = fileName(file.path).toLowerCase();
	const fuzzyMatch = fuzzyMatchText(file.path, normalizedQuery);
	if (!fuzzyMatch.matched) return null;

	if (path === normalizedQuery) return 0;
	if (name === normalizedQuery) return 1;
	if (name.startsWith(normalizedQuery)) return 2;
	if (path.startsWith(normalizedQuery)) return 3;
	if (name.includes(normalizedQuery)) return 4;
	if (path.includes(normalizedQuery)) return 5;
	return 6 + path.length / 1000;
};

const findFiles = (files, query) =>
	files
		.map((file, index) => ({
			file,
			index,
			score: scoreFileMatch(file, query),
		}))
		.filter((item) => item.score !== null)
		.sort((a, b) => a.score - b.score || a.index - b.index)
		.slice(0, 30)
		.map((item) => item.file);

const diffSearchIndexOf = (value, query, caseInsensitive) => {
	const source = String(value ?? "");
	const normalizedQuery = normalizeSearchQuery(query);
	if (!normalizedQuery) return -1;

	const haystack = caseInsensitive ? source.toLowerCase() : source;
	const needle = caseInsensitive
		? normalizedQuery.toLowerCase()
		: normalizedQuery;

	return haystack.indexOf(needle);
};

const getDiffLineSearchText = (rawLine) => {
	if (/^[ +-]/.test(rawLine)) return rawLine.slice(1);
	return rawLine;
};

const collectDiffSearchMatches = (files, query, caseInsensitive) => {
	const normalizedQuery = normalizeSearchQuery(query);
	if (!normalizedQuery) return [];

	const results = [];

	for (const file of files || []) {
		let oldLine = 0;
		let newLine = 0;
		let inHunk = false;
		const rawLines = String(file.diff || "")
			.replace(/\r\n?/g, "\n")
			.split("\n");

		rawLines.forEach((rawLine, rawIndex) => {
			const hunk = rawLine.match(/^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/);
			if (hunk) {
				oldLine = Number(hunk[1]);
				newLine = Number(hunk[2]);
				inHunk = true;
				return;
			}

			if (!inHunk || rawLine.startsWith("\\ No newline")) return;

			const prefix = rawLine[0] || " ";
			const lineText = getDiffLineSearchText(rawLine);
			const matchIndex = diffSearchIndexOf(
				lineText,
				normalizedQuery,
				caseInsensitive,
			);
			const oldLineNumber = prefix === "+" ? null : oldLine;
			const newLineNumber = prefix === "-" ? null : newLine;
			const side = prefix === "-" ? SplitSide.old : SplitSide.new;
			const lineNumber = side === SplitSide.old ? oldLineNumber : newLineNumber;

			if (matchIndex >= 0 && lineNumber) {
				results.push({
					id: `${file.id}:line:${rawIndex}`,
					file,
					text: lineText,
					matchIndex,
					prefix: prefix === "+" || prefix === "-" ? prefix : " ",
					oldLine: oldLineNumber,
					newLine: newLineNumber,
					side,
					lineNumber,
				});
			}

			if (prefix === "+") {
				newLine += 1;
				return;
			}
			if (prefix === "-") {
				oldLine += 1;
				return;
			}
			oldLine += 1;
			newLine += 1;
		});
	}

	return results;
};

const formatDiffSearchResultLocation = (result) => {
	if (!result) return "";
	return `${result.file.path}:${result.lineNumber}`;
};

const discussionMatchesFile = (discussion, file) => {
	if (!discussion || !file) return false;
	return (
		discussion.newPath === file.newPath ||
		discussion.oldPath === file.oldPath ||
		discussion.newPath === file.path ||
		discussion.oldPath === file.path
	);
};

const highlightedCodeNodes = (code, language) => {
	const normalizedLanguage = String(language || "").trim();
	if (
		normalizedLanguage &&
		normalizedLanguage !== "txt" &&
		codeHighlighter.registered(normalizedLanguage)
	) {
		try {
			return codeHighlighter.highlight(normalizedLanguage, code || " ")
				.children;
		} catch (error) {
			console.warn("Failed to highlight referenced code:", error);
		}
	}

	return [{ type: "text", value: code || " " }];
};

const buildCodeReference = (file, side, lineNumber) => {
	const targetLine = readPositiveLineNumber(lineNumber);
	if (!file || !targetLine) return null;

	const lines = parseDiffCodeLines(file, splitSideFromString(side));
	const targetIndex = lines.findIndex((line) => line.lineNumber === targetLine);
	if (targetIndex < 0) return null;

	const startIndex = Math.max(0, targetIndex - COMMENT_CODE_CONTEXT_LINES);
	const endIndex = Math.min(
		lines.length,
		targetIndex + COMMENT_CODE_CONTEXT_LINES + 1,
	);
	const language = file.lang && file.lang !== "txt" ? file.lang : "";

	return {
		fileId: file.id,
		filePath: file.path,
		language,
		side,
		lineNumber: targetLine,
		lines: lines.slice(startIndex, endIndex).map((line) => ({
			...line,
			highlightNodes: highlightedCodeNodes(line.text, language),
			target: line.lineNumber === targetLine,
		})),
	};
};

const buildCommentExtendData = (file, discussions, visible) => {
	const extendData = { oldFile: {}, newFile: {} };
	if (!visible || !file) return extendData;

	for (const discussion of discussions || []) {
		if (!discussionMatchesFile(discussion, file)) continue;

		const side = discussion.side === "old" ? "old" : "new";
		const lineNumber = side === "old" ? discussion.oldLine : discussion.newLine;
		if (!lineNumber) continue;

		const target = side === "old" ? extendData.oldFile : extendData.newFile;
		if (!target[lineNumber]) {
			target[lineNumber] = {
				data: {
					fileId: file.id,
					filePath: file.path,
					side,
					lineNumber,
					discussions: [],
				},
			};
		}
		target[lineNumber].data.discussions.push(discussion);
	}

	return extendData;
};

const buildDiscussionCodeReferences = (files, discussions) => {
	const references = new Map();

	for (const discussion of discussions || []) {
		const file = (files || []).find((candidate) =>
			discussionMatchesFile(discussion, candidate),
		);
		if (!file) continue;

		const side = discussion.side === "old" ? "old" : "new";
		const lineNumber = side === "old" ? discussion.oldLine : discussion.newLine;
		const reference = buildCodeReference(file, side, lineNumber);
		if (reference) references.set(discussion.id, reference);
	}

	return references;
};

const countDiscussionNotes = (discussions = []) =>
	discussions.reduce(
		(count, discussion) => count + (discussion.notes?.length || 0),
		0,
	);

const countLoadedMRNotes = (comments) =>
	Array.isArray(comments?.allNotes) ? comments.allNotes.length : 0;

const getDisplayedMRCommentCount = (comments, mr) =>
	comments?.loaded
		? countLoadedMRNotes(comments)
		: Number(mr?.userNotesCount || 0);

const formatLineSide = (side) => (side === "old" ? "old line" : "new line");

const MARKDOWN_ACTIONS = [
	{ id: "bold", label: "Bold", icon: Bold },
	{ id: "italic", label: "Italic", icon: Italic },
	{ id: "code", label: "Code", icon: Code },
	{ id: "quote", label: "Quote", icon: Quote },
	{ id: "list", label: "List", icon: List },
	{ id: "link", label: "Link", icon: Link },
];

const prefixSelectedLines = (text, prefix) =>
	text
		.split("\n")
		.map((line) => `${prefix}${line || ""}`)
		.join("\n");

const applyMarkdownAction = ({ actionId, textarea, value, onChange }) => {
	if (!textarea) return;

	const start = textarea.selectionStart ?? value.length;
	const end = textarea.selectionEnd ?? value.length;
	const before = value.slice(0, start);
	const selected = value.slice(start, end);
	const after = value.slice(end);
	const fallback = {
		bold: "bold text",
		italic: "italic text",
		code: "code",
		quote: "quote",
		list: "list item",
		link: "link text",
	}[actionId];
	const source = selected || fallback || "";
	let nextText = source;
	let selectionStart = start;
	let selectionEnd = start + source.length;

	if (actionId === "bold") {
		nextText = `**${source}**`;
		selectionStart = start + 2;
		selectionEnd = selectionStart + source.length;
	} else if (actionId === "italic") {
		nextText = `*${source}*`;
		selectionStart = start + 1;
		selectionEnd = selectionStart + source.length;
	} else if (actionId === "code") {
		if (source.includes("\n")) {
			nextText = `\`\`\`\n${source}\n\`\`\``;
			selectionStart = start + 4;
			selectionEnd = selectionStart + source.length;
		} else {
			nextText = `\`${source}\``;
			selectionStart = start + 1;
			selectionEnd = selectionStart + source.length;
		}
	} else if (actionId === "quote") {
		nextText = prefixSelectedLines(source, "> ");
		selectionStart = start + 2;
		selectionEnd = start + nextText.length;
	} else if (actionId === "list") {
		nextText = prefixSelectedLines(source, "- ");
		selectionStart = start + 2;
		selectionEnd = start + nextText.length;
	} else if (actionId === "link") {
		nextText = `[${source}](url)`;
		selectionStart = start + nextText.length - 4;
		selectionEnd = start + nextText.length - 1;
	}

	onChange(`${before}${nextText}${after}`);
	window.requestAnimationFrame(() => {
		textarea.focus();
		textarea.setSelectionRange(selectionStart, selectionEnd);
	});
};

const MarkdownFormattingToolbar = ({
	textareaRef,
	value,
	onChange,
	disabled = false,
}) => (
	<div className="flex items-center gap-0.5 border-b border-[var(--color-border)] bg-[var(--color-bg-primary)] px-1.5 py-1">
		{MARKDOWN_ACTIONS.map((action) => {
			const Icon = action.icon;
			return (
				<Tooltip key={action.id} content={action.label} placement="bottom">
					<button
						type="button"
						onClick={() =>
							applyMarkdownAction({
								actionId: action.id,
								textarea: textareaRef.current,
								value,
								onChange,
							})
						}
						disabled={disabled}
						className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-border-hover)] disabled:cursor-not-allowed disabled:opacity-50"
						aria-label={action.label}
					>
						<Icon className="h-3.5 w-3.5" />
					</button>
				</Tooltip>
			);
		})}
	</div>
);

const MarkdownEditor = ({
	value,
	onChange,
	textareaRef,
	placeholder,
	disabled = false,
	minHeightClass = "min-h-20",
	textSizeClass = "text-xs",
}) => (
	<div className="overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] focus-within:border-[var(--color-border-hover)]">
		<MarkdownFormattingToolbar
			textareaRef={textareaRef}
			value={value}
			onChange={onChange}
			disabled={disabled}
		/>
		<textarea
			ref={textareaRef}
			value={value}
			onChange={(event) => onChange(event.target.value)}
			placeholder={placeholder}
			disabled={disabled}
			className={`diff-markdown-textarea ${minHeightClass} block w-full resize-y appearance-none border-0 bg-transparent px-3 py-2 ${textSizeClass} text-[var(--color-text-primary)] shadow-none outline-none placeholder:text-[var(--color-text-tertiary)] focus:border-0 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60`}
		/>
	</div>
);

const DiffCommentWidget = ({
	file,
	lineNumber,
	side,
	onClose,
	onCreateComment,
	onCommentCreated,
}) => {
	const [body, setBody] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState(null);
	const [submitted, setSubmitted] = useState(false);
	const textareaRef = useRef(null);
	const sideLabel = side === SplitSide.old ? "old" : "new";

	useEffect(() => {
		textareaRef.current?.focus();
	}, []);

	const submit = async (event) => {
		event.preventDefault();
		const trimmedBody = body.trim();
		if (!trimmedBody || submitting) return;

		setSubmitting(true);
		setError(null);

		try {
			await onCreateComment({
				file,
				lineNumber,
				side,
				body: trimmedBody,
			});
			setSubmitted(true);
			onCommentCreated?.(`Comment added on ${file.path}:${lineNumber}`);
			window.setTimeout(onClose, 650);
		} catch (err) {
			setError(err.message || "Failed to add comment");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form
			onSubmit={submit}
			onKeyDownCapture={(event) => {
				if (event.key !== "Escape") return;
				consumeEscapeKey(event);
				onClose();
			}}
			data-diff-comment-editor="true"
			className="mx-3 my-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-3 shadow-lg"
		>
			<div className="mb-2 flex items-center justify-between gap-3 text-xs text-[var(--color-text-secondary)]">
				<span>
					{file.path}:{lineNumber} ({sideLabel})
				</span>
				<button
					type="button"
					onClick={onClose}
					className="rounded-md p-1 text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
					aria-label="Close comment form"
				>
					<X className="h-4 w-4" />
				</button>
			</div>
			<MarkdownEditor
				textareaRef={textareaRef}
				value={body}
				onChange={setBody}
				placeholder="Leave a code comment"
				disabled={submitting || submitted}
				minHeightClass="min-h-24"
				textSizeClass="text-sm"
			/>
			{error && (
				<div className="mt-2 flex items-center gap-2 rounded border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-2 py-1.5 text-xs text-[var(--color-error-text)]">
					<AlertCircle className="h-3.5 w-3.5 shrink-0" />
					<span>{error}</span>
				</div>
			)}
			{submitted && (
				<div className="mt-2 flex items-center gap-2 rounded border border-green-500/30 bg-green-500/10 px-2 py-1.5 text-xs text-green-600 dark:text-green-400">
					<CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
					<span>Comment added</span>
				</div>
			)}
			<div className="mt-3 flex items-center justify-end gap-2">
				<button
					type="button"
					onClick={onClose}
					className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
				>
					Cancel
				</button>
				<button
					type="submit"
					disabled={!body.trim() || submitting || submitted}
					className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-border-hover)] disabled:cursor-not-allowed disabled:opacity-50"
				>
					{submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
					Comment
				</button>
			</div>
		</form>
	);
};

const renderHighlightedCodeNode = (node, key) => {
	if (!node) return null;
	if (node.type === "text") {
		return <React.Fragment key={key}>{node.value}</React.Fragment>;
	}
	if (node.type !== "element") return null;

	const className = Array.isArray(node.properties?.className)
		? node.properties.className.join(" ")
		: "";

	return (
		<span key={key} className={className || undefined}>
			{(node.children || []).map((child, index) =>
				renderHighlightedCodeNode(child, `${key}-${index}`),
			)}
		</span>
	);
};

const ReferencedCodeSnippet = ({ reference, onJumpToDiff }) => {
	if (!reference?.lines?.length) return null;

	const languageLabel = reference.language || "text";

	return (
		<div className="mr-diff-code-reference mt-3 overflow-hidden rounded-md">
			<div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-code-border)] px-3 py-2">
				<div className="flex min-w-0 items-center gap-2">
					<Code className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-tertiary)]" />
					<span className="truncate text-xs font-medium text-[var(--color-text-secondary)]">
						{reference.filePath}:{reference.lineNumber}
					</span>
					<span className="rounded-full border border-[var(--color-code-border)] px-1.5 py-0.5 font-mono text-[10px] uppercase text-[var(--color-text-tertiary)]">
						{languageLabel}
					</span>
				</div>
				<button
					type="button"
					onClick={onJumpToDiff}
					className="inline-flex h-7 items-center gap-1.5 rounded-md border border-[var(--color-code-border)] px-2 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
				>
					<LocateFixed className="h-3.5 w-3.5" />
					Jump to diff
				</button>
			</div>
			<pre className="m-0 overflow-x-auto p-0" data-language={languageLabel}>
				<code className="block py-2 text-xs leading-5">
					{reference.lines.map((line) => (
						<span
							key={line.lineNumber}
							className={`mr-diff-code-reference-line ${
								line.target ? "is-target" : ""
							}`}
						>
							<span className="mr-diff-code-reference-line-number">
								{line.lineNumber}
							</span>
							<span className="mr-diff-code-reference-line-code">
								{(line.highlightNodes || []).map((node, index) =>
									renderHighlightedCodeNode(
										node,
										`${line.lineNumber}-${index}`,
									),
								)}
							</span>
						</span>
					))}
				</code>
			</pre>
		</div>
	);
};

const InlineCommentNote = ({
	note,
	onUpdate,
	onDelete,
	onReact,
	reactingKey,
	onNotice,
	isReply = false,
}) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(note.body || "");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const editTextareaRef = useRef(null);
	const edited =
		note.updatedAt &&
		note.createdAt &&
		new Date(note.updatedAt).getTime() !== new Date(note.createdAt).getTime();

	useEffect(() => {
		if (!editing) setDraft(note.body || "");
	}, [editing, note.body]);

	const submitEdit = async (event) => {
		event.preventDefault();
		const body = draft.trim();
		if (!body || busy) return;

		setBusy(true);
		setError(null);
		try {
			await onUpdate({
				discussionId: note.discussionId,
				noteId: note.id,
				body,
			});
			setEditing(false);
			onNotice?.("Comment updated");
		} catch (err) {
			setError(err.message || "Failed to update comment");
		} finally {
			setBusy(false);
		}
	};

	const deleteNote = async () => {
		if (busy || !window.confirm("Delete this comment?")) return;

		setBusy(true);
		setError(null);
		try {
			await onDelete({
				discussionId: note.discussionId,
				noteId: note.id,
			});
			onNotice?.("Comment deleted");
		} catch (err) {
			setError(err.message || "Failed to delete comment");
			setBusy(false);
		}
	};

	const addReaction = async (targetNote, name) => {
		if (!onReact) return;

		setBusy(true);
		setError(null);
		try {
			await onReact({ noteId: targetNote.restId || targetNote.id, name });
			onNotice?.("Emoji reaction added");
		} catch (err) {
			setError(err.message || "Failed to add emoji reaction");
			throw err;
		} finally {
			setBusy(false);
		}
	};

	return (
		<article
			className={`group/comment mr-diff-thread-note min-w-0 rounded-md p-3 ${
				isReply ? "mr-diff-thread-note-reply ml-6 mt-2" : ""
			}`}
		>
			<div className="flex min-w-0 items-start gap-2.5">
				<Avatar
					alt={note.author?.name}
					src={note.author?.avatarUrl}
					size="md"
					className="mt-0.5 ring-2 ring-[var(--color-comment-surface)]"
				/>
				<div className="min-w-0 flex-1">
					<div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
						<span className="truncate font-medium text-[var(--color-text-primary)]">
							{note.author?.name || "Unknown user"}
						</span>
						{note.createdAt && (
							<span className="inline-flex shrink-0 items-center gap-1 text-[var(--color-text-tertiary)]">
								<Clock className="h-3 w-3" />
								<DateTime date={note.createdAt} />
							</span>
						)}
						{edited && (
							<span className="shrink-0 text-[10px] text-[var(--color-text-tertiary)]">
								edited
							</span>
						)}
						<div className="ml-auto flex shrink-0 items-center gap-0.5 rounded-md bg-[var(--color-comment-surface-soft)] p-0.5 opacity-0 transition-opacity group-hover/comment:opacity-100 focus-within:opacity-100">
							{safeHttpUrl(note.url) && (
								<a
									href={safeHttpUrl(note.url)}
									target="_blank"
									rel="noopener noreferrer"
									onClick={(event) => event.stopPropagation()}
									className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-link)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
									aria-label="Open comment on GitLab"
									title="Open comment on GitLab"
								>
									<ExternalLink className="h-3.5 w-3.5" />
								</a>
							)}
							<button
								type="button"
								onClick={() => setEditing((value) => !value)}
								disabled={busy}
								className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-50"
								aria-label="Edit comment"
								title="Edit comment"
							>
								<Edit3 className="h-3.5 w-3.5" />
							</button>
							<button
								type="button"
								onClick={deleteNote}
								disabled={busy}
								className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/50 disabled:cursor-not-allowed disabled:opacity-50"
								aria-label="Delete comment"
								title="Delete comment"
							>
								<Trash2 className="h-3.5 w-3.5" />
							</button>
						</div>
					</div>
					{editing ? (
						<form
							onSubmit={submitEdit}
							onKeyDownCapture={(event) => {
								if (event.key !== "Escape") return;
								consumeEscapeKey(event);
								setEditing(false);
							}}
							data-diff-comment-editor="true"
							className="mt-2 space-y-2"
						>
							<MarkdownEditor
								textareaRef={editTextareaRef}
								value={draft}
								onChange={setDraft}
								disabled={busy}
								placeholder="Edit comment"
							/>
							<div className="flex items-center justify-end gap-1.5">
								<button
									type="button"
									onClick={() => setEditing(false)}
									disabled={busy}
									className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-50"
									aria-label="Cancel edit"
									title="Cancel"
								>
									<X className="h-3.5 w-3.5" />
								</button>
								<button
									type="submit"
									disabled={!draft.trim() || busy}
									className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-border-hover)] disabled:cursor-not-allowed disabled:opacity-50"
									aria-label="Save comment"
									title="Save"
								>
									{busy ? (
										<Loader2 className="h-3.5 w-3.5 animate-spin" />
									) : (
										<Save className="h-3.5 w-3.5" />
									)}
								</button>
							</div>
						</form>
					) : (
						<CommentBody
							note={note}
							className="mt-2 text-sm leading-relaxed text-[var(--color-text-primary)]"
						/>
					)}
					<EmojiReactionBar
						note={note}
						onReact={addReaction}
						canReact={Boolean(onReact) && !busy}
						reactingKey={reactingKey}
						compact
					/>
					{error && (
						<div className="mt-2 flex items-center gap-2 rounded border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-2 py-1.5 text-xs text-[var(--color-error-text)]">
							<AlertCircle className="h-3.5 w-3.5 shrink-0" />
							{error}
						</div>
					)}
				</div>
			</div>
		</article>
	);
};

const InlineReplyForm = ({ discussionId, onReply, onCancel, onNotice }) => {
	const [body, setBody] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const textareaRef = useRef(null);

	useEffect(() => {
		window.requestAnimationFrame(() => textareaRef.current?.focus());
	}, []);

	const submitReply = async (event) => {
		event.preventDefault();
		const trimmedBody = body.trim();
		if (!trimmedBody || busy) return;

		setBusy(true);
		setError(null);
		try {
			await onReply({ discussionId, body: trimmedBody });
			setBody("");
			onNotice?.("Reply added");
			onCancel();
		} catch (err) {
			setError(err.message || "Failed to add reply");
		} finally {
			setBusy(false);
		}
	};

	return (
		<form
			onSubmit={submitReply}
			onKeyDownCapture={(event) => {
				if (event.key !== "Escape") return;
				consumeEscapeKey(event);
				onCancel();
			}}
			data-diff-comment-editor="true"
			className="mr-diff-thread-reply-form space-y-2 rounded-md p-2"
		>
			<MarkdownEditor
				textareaRef={textareaRef}
				value={body}
				onChange={setBody}
				placeholder="Reply to thread"
				disabled={busy}
				minHeightClass="min-h-20"
			/>
			{error && (
				<div className="rounded border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-2 py-1 text-xs text-[var(--color-error-text)]">
					{error}
				</div>
			)}
			<div className="flex items-center justify-end gap-1.5">
				<button
					type="button"
					onClick={onCancel}
					disabled={busy}
					className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-50"
					aria-label="Cancel reply"
					title="Cancel"
				>
					<X className="h-3.5 w-3.5" />
				</button>
				<button
					type="submit"
					disabled={!body.trim() || busy}
					className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-border-hover)] disabled:cursor-not-allowed disabled:opacity-50"
					aria-label="Send reply"
					title="Send reply"
				>
					{busy ? (
						<Loader2 className="h-3.5 w-3.5 animate-spin" />
					) : (
						<Send className="h-3.5 w-3.5" />
					)}
				</button>
			</div>
		</form>
	);
};

const InlineCommentThread = ({
	data,
	onUpdateComment,
	onDeleteComment,
	onReplyComment,
	onReactComment,
	reactingEmojiKey,
	onNotice,
}) => {
	const [replyingDiscussionId, setReplyingDiscussionId] = useState(null);
	const noteCount = countDiscussionNotes(data.discussions);

	return (
		<div
			data-diff-comment-thread="true"
			data-comment-thread-id={`${data.fileId}:${data.side}:${data.lineNumber}`}
			data-file-id={data.fileId}
			data-file-path={data.filePath}
			tabIndex={-1}
			className="mr-diff-thread-shell mx-3 my-2 rounded-lg px-3 py-3"
			onMouseDown={(event) => event.stopPropagation()}
		>
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="flex min-w-0 items-center gap-2.5">
					<span className="mr-diff-thread-icon inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
						<MessagesSquare className="h-4 w-4" />
					</span>
					<div className="min-w-0">
						<div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-[var(--color-text-primary)]">
							<span>
								{noteCount} {noteCount === 1 ? "comment" : "comments"}
							</span>
							<span className="font-medium text-[var(--color-text-tertiary)]">
								on
							</span>
							<span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-comment-surface-soft)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-text-secondary)]">
								<Code className="h-3 w-3" />
								{formatLineSide(data.side)} {data.lineNumber}
							</span>
						</div>
					</div>
				</div>
				<span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-comment-surface-soft)] px-2 py-1 text-[11px] font-medium text-[var(--color-text-secondary)]">
					<MessageSquare className="h-3 w-3" />
					Review thread
				</span>
			</div>
			<div className="mt-3 space-y-3">
				{data.discussions.map((discussion, discussionIndex) => (
					<div
						key={discussion.id}
						className={
							discussionIndex > 0
								? "border-t border-[var(--color-border)] pt-3"
								: ""
						}
					>
						{discussion.notes.map((note, noteIndex) => (
							<InlineCommentNote
								key={note.id}
								note={note}
								isReply={noteIndex > 0}
								onUpdate={onUpdateComment}
								onDelete={onDeleteComment}
								onReact={onReactComment}
								reactingKey={reactingEmojiKey}
								onNotice={onNotice}
							/>
						))}
						<div className="mt-2 flex items-start gap-2 pl-6">
							<span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-comment-surface-soft)] text-[var(--color-text-tertiary)]">
								<CornerDownRight className="h-3.5 w-3.5" />
							</span>
							{replyingDiscussionId === discussion.id ? (
								<div className="min-w-0 flex-1">
									<InlineReplyForm
										discussionId={discussion.id}
										onReply={onReplyComment}
										onCancel={() => setReplyingDiscussionId(null)}
										onNotice={onNotice}
									/>
								</div>
							) : (
								<button
									type="button"
									onClick={() => setReplyingDiscussionId(discussion.id)}
									className="inline-flex h-8 items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-comment-surface-soft)] px-2.5 text-xs font-medium text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
								>
									<Reply className="h-3.5 w-3.5" />
									Reply
								</button>
							)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

const FileTreeNode = ({
	node,
	expandedDirs,
	onToggleDir,
	onSelectFile,
	onContextMenu,
	activeFileId,
	forceExpanded = false,
}) => {
	if (node.type === "file") {
		const status = fileStatus(node.file);
		const selected = activeFileId === node.file.id;

		return (
			<button
				type="button"
				data-diff-file-nav-id={node.file.id}
				onClick={() => onSelectFile(node.file)}
				onContextMenu={(event) => onContextMenu(event, node)}
				className={`group/file flex h-8 w-full min-w-0 items-center gap-1.5 rounded border px-1.5 text-left text-xs outline-none transition-[background-color,border-color,box-shadow,color,opacity] duration-75 ease-out focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] ${
					selected
						? "border-[var(--color-border-hover)] bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] shadow-sm hover:opacity-95"
						: "border-transparent text-[var(--color-text-secondary)] hover:border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
				}`}
			>
				<FileKindIcon
					path={node.path}
					className={`h-4 w-4 shrink-0 transition-colors duration-75 ${
						selected
							? "text-[var(--color-text-secondary)]"
							: "text-[var(--color-text-tertiary)] group-hover/file:text-[var(--color-text-secondary)]"
					}`}
				/>
				<span className="min-w-0 flex-1 truncate">{node.name}</span>
				<span
					className={`inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded border px-1 font-mono text-[9px] font-semibold ${status.badgeClassName}`}
				>
					{status.label}
				</span>
			</button>
		);
	}

	const expanded = forceExpanded || (expandedDirs[node.path] ?? true);
	const Icon = expanded ? FolderOpen : Folder;

	return (
		<div>
			<button
				type="button"
				onClick={() => onToggleDir(node.path)}
				onContextMenu={(event) => onContextMenu(event, node)}
				className="group/folder flex h-8 w-full min-w-0 items-center gap-1.5 rounded border border-transparent px-1.5 text-left text-xs font-medium text-[var(--color-text-secondary)] outline-none transition-[background-color,border-color,color] duration-75 ease-out hover:border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
			>
				{expanded ? (
					<ChevronDown className="h-3.5 w-3.5 shrink-0" />
				) : (
					<ChevronRight className="h-3.5 w-3.5 shrink-0" />
				)}
				<Icon className="h-4 w-4 shrink-0 text-[var(--color-text-tertiary)] transition-colors duration-75 group-hover/folder:text-[var(--color-text-secondary)]" />
				<span className="min-w-0 flex-1 truncate">{node.name}</span>
			</button>
			{expanded && node.children.length > 0 && (
				<div className="ml-2.5 border-l border-transparent pl-1.5">
					{node.children.map((child) => (
						<FileTreeNode
							key={child.path}
							node={child}
							expandedDirs={expandedDirs}
							onToggleDir={onToggleDir}
							onSelectFile={onSelectFile}
							onContextMenu={onContextMenu}
							activeFileId={activeFileId}
							forceExpanded={forceExpanded}
						/>
					))}
				</div>
			)}
		</div>
	);
};

const FileTreeContextMenu = ({
	menu,
	onCopyPath,
	onCopyGitLabLink,
	onClose,
}) => {
	if (!menu) return null;

	return (
		<div
			role="menu"
			className="fixed z-[240] min-w-48 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] py-1 shadow-xl"
			style={{ left: menu.x, top: menu.y }}
			onMouseDown={(event) => event.stopPropagation()}
		>
			<button
				type="button"
				role="menuitem"
				onClick={() => {
					onCopyPath(menu.path);
					onClose();
				}}
				className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
			>
				<Copy className="h-4 w-4 text-[var(--color-text-secondary)]" />
				Copy relative path
			</button>
			<button
				type="button"
				role="menuitem"
				onClick={() => {
					onCopyGitLabLink(menu);
					onClose();
				}}
				className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
			>
				<ExternalLink className="h-4 w-4 text-[var(--color-text-secondary)]" />
				Copy GitLab link
			</button>
		</div>
	);
};

const DiffLineContextMenu = ({
	menu,
	onCopyFormattedCode,
	onCopyGitLabLink,
	onClose,
}) => {
	if (!menu) return null;

	return (
		<div
			role="menu"
			className="fixed z-[245] min-w-52 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] py-1 shadow-xl"
			style={{ left: menu.x, top: menu.y }}
			onMouseDown={(event) => event.stopPropagation()}
			onContextMenu={(event) => {
				event.preventDefault();
				event.stopPropagation();
			}}
		>
			<button
				type="button"
				role="menuitem"
				onClick={() => {
					onCopyFormattedCode(menu);
					onClose();
				}}
				className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] transition-colors duration-75 hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
			>
				{menu.selectedText ? (
					<Copy className="h-4 w-4 text-[var(--color-text-secondary)]" />
				) : (
					<Code className="h-4 w-4 text-[var(--color-text-secondary)]" />
				)}
				{menu.selectedText ? "Copy selected text" : "Copy formatted code"}
			</button>
			<button
				type="button"
				role="menuitem"
				onClick={() => {
					onCopyGitLabLink(menu);
					onClose();
				}}
				className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] transition-colors duration-75 hover:bg-[var(--color-bg-secondary)] focus:bg-[var(--color-bg-secondary)] focus:outline-none"
			>
				<ExternalLink className="h-4 w-4 text-[var(--color-text-secondary)]" />
				Copy link to GitLab
			</button>
		</div>
	);
};

const FileSwitcher = ({ open, files, onSelectFile, onClose }) => {
	const [query, setQuery] = useState("");
	const [activeIndex, setActiveIndex] = useState(0);
	const inputRef = useRef(null);
	const results = useMemo(() => findFiles(files, query), [files, query]);

	useEffect(() => {
		if (!open) return;

		setActiveIndex(0);
		window.requestAnimationFrame(() => {
			inputRef.current?.focus();
			inputRef.current?.select();
		});
	}, [open]);

	useEffect(() => {
		if (!open) return undefined;

		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				event.preventDefault();
				onClose();
				return;
			}
			if (event.key === "ArrowDown") {
				event.preventDefault();
				setActiveIndex((index) =>
					Math.min(index + 1, Math.max(0, results.length - 1)),
				);
				return;
			}
			if (event.key === "ArrowUp") {
				event.preventDefault();
				setActiveIndex((index) => Math.max(0, index - 1));
				return;
			}
			if (event.key === "Enter") {
				event.preventDefault();
				const selectedFile = results[activeIndex];
				if (!selectedFile) return;
				setQuery("");
				setActiveIndex(0);
				onSelectFile(selectedFile);
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [activeIndex, onClose, onSelectFile, open, results]);

	if (!open) return null;

	return (
		<div
			className="diff-fast-motion fixed inset-0 z-[230] bg-black/35 px-3 pt-[12vh]"
			onMouseDown={(event) => {
				if (event.target === event.currentTarget) onClose();
			}}
		>
			<div className="mx-auto max-w-2xl overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] shadow-2xl">
				<div className="border-b border-[var(--color-border)] p-2">
					<div className="diff-search-shell flex h-10 items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-2.5 transition-[border-color,box-shadow]">
						<Search className="h-4 w-4 shrink-0 text-[var(--color-text-tertiary)]" />
						<input
							ref={inputRef}
							type="text"
							value={query}
							onChange={(event) => {
								setQuery(event.target.value);
								setActiveIndex(0);
							}}
							placeholder="Search files"
							className="diff-search-input h-full min-w-0 flex-1 bg-transparent text-sm leading-normal text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)]"
						/>
						<button
							type="button"
							onClick={onClose}
							className="no-hover-lift inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-primary)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
							aria-label="Close file search"
						>
							<X className="h-4 w-4" />
						</button>
					</div>
				</div>
				<div className="max-h-[min(28rem,60vh)] overflow-y-auto p-1">
					{results.length > 0 ? (
						results.map((file, index) => {
							const status = fileStatus(file);
							const active = index === activeIndex;
							return (
								<button
									key={file.id}
									type="button"
									onMouseEnter={() => setActiveIndex(index)}
									onClick={() => {
										setQuery("");
										setActiveIndex(0);
										onSelectFile(file);
									}}
									className={`no-hover-lift flex w-full min-w-0 items-center gap-2 rounded border px-2 py-2 text-left text-sm outline-none transition-[background-color,border-color,color] ${
										active
											? "border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]"
											: "border-transparent text-[var(--color-text-secondary)] hover:border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
									}`}
								>
									<FileKindIcon
										path={file.path}
										className="h-4 w-4 shrink-0 text-[var(--color-text-tertiary)]"
									/>
									<span className="min-w-0 flex-1 truncate">{file.path}</span>
									<span
										className={`shrink-0 font-mono text-[10px] ${status.className}`}
									>
										{status.label}
									</span>
									<span className="shrink-0 font-mono text-xs text-green-600 dark:text-green-400">
										+{file.additions}
									</span>
									<span className="shrink-0 font-mono text-xs text-red-600 dark:text-red-400">
										-{file.deletions}
									</span>
								</button>
							);
						})
					) : (
						<div className="px-3 py-8 text-center text-sm text-[var(--color-text-secondary)]">
							No matching files
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const DiffTextSearchBar = ({
	query,
	onQueryChange,
	caseInsensitive,
	onCaseInsensitiveChange,
	resultCount,
	activeIndex,
	activeResult,
	onPrevious,
	onNext,
	onClose,
	inputRef,
}) => {
	const hasQuery = normalizeSearchQuery(query).length > 0;
	const resultLabel = !hasQuery
		? "Type to search"
		: resultCount > 0
			? `${activeIndex + 1} of ${resultCount}`
			: "No matches";
	const locationLabel = formatDiffSearchResultLocation(activeResult);

	useEffect(() => {
		window.requestAnimationFrame(() => {
			inputRef.current?.focus();
			inputRef.current?.select();
		});
	}, [inputRef]);

	return (
		<div className="diff-fast-motion shrink-0 border-b border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-2">
			<div className="flex flex-wrap items-center gap-2">
				<div className="diff-search-shell flex h-9 min-w-[min(28rem,100%)] flex-1 items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-2.5 transition-[border-color,box-shadow]">
					<Search className="h-4 w-4 shrink-0 text-[var(--color-text-tertiary)]" />
					<input
						ref={inputRef}
						type="text"
						value={query}
						onChange={(event) => onQueryChange(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Escape") {
								event.preventDefault();
								event.stopPropagation();
								onClose();
								return;
							}
							if (event.key === "Enter") {
								event.preventDefault();
								if (event.shiftKey) {
									onPrevious();
								} else {
									onNext();
								}
							}
						}}
						placeholder="Search changed lines across files"
						className="diff-search-input h-full min-w-0 flex-1 bg-transparent text-sm leading-normal text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)]"
						aria-label="Search diff text"
					/>
					<span className="shrink-0 font-mono text-xs tabular-nums text-[var(--color-text-tertiary)]">
						{resultLabel}
					</span>
					{query && (
						<button
							type="button"
							onClick={() => onQueryChange("")}
							className="no-hover-lift inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-primary)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
							aria-label="Clear diff search"
						>
							<X className="h-3.5 w-3.5" />
						</button>
					)}
				</div>
				<label className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-[var(--color-border)] px-2.5 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]">
					<input
						type="checkbox"
						checked={caseInsensitive}
						onChange={(event) => onCaseInsensitiveChange(event.target.checked)}
						className="h-3.5 w-3.5 accent-[var(--color-accent)]"
					/>
					Case insensitive
				</label>
				<div className="inline-flex h-9 shrink-0 rounded-md border border-[var(--color-border)] p-0.5">
					<Tooltip content="Previous match (Shift+Enter)" placement="bottom">
						<button
							type="button"
							onClick={onPrevious}
							disabled={resultCount === 0}
							className="inline-flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-45"
							aria-label="Previous diff search match"
						>
							<ArrowUp className="h-4 w-4" />
						</button>
					</Tooltip>
					<Tooltip content="Next match (Enter)" placement="bottom">
						<button
							type="button"
							onClick={onNext}
							disabled={resultCount === 0}
							className="inline-flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-45"
							aria-label="Next diff search match"
						>
							<ArrowDown className="h-4 w-4" />
						</button>
					</Tooltip>
				</div>
				{locationLabel && (
					<div className="min-w-0 flex-1 truncate text-xs text-[var(--color-text-tertiary)]">
						{locationLabel}
					</div>
				)}
				<button
					type="button"
					onClick={onClose}
					className="no-hover-lift inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
					aria-label="Close diff search"
				>
					<X className="h-4 w-4" />
				</button>
			</div>
		</div>
	);
};

const getLineNumberContext = (target) => {
	if (!(target instanceof Element)) return null;

	const numberedTarget = target.closest(
		"span[data-line-new-num], span[data-line-old-num], span[data-line-num]",
	);
	if (numberedTarget) {
		const oldLine = readPositiveLineNumber(
			numberedTarget.getAttribute("data-line-old-num"),
		);
		const newLine = readPositiveLineNumber(
			numberedTarget.getAttribute("data-line-new-num"),
		);
		const splitLine = readPositiveLineNumber(
			numberedTarget.getAttribute("data-line-num"),
		);

		if (oldLine) return { lineNumber: oldLine, side: SplitSide.old };
		if (newLine) return { lineNumber: newLine, side: SplitSide.new };
		if (splitLine) {
			return {
				lineNumber: splitLine,
				side: splitSideFromString(
					numberedTarget.closest("[data-side]")?.getAttribute("data-side"),
				),
			};
		}
	}

	const splitContent = target.closest(
		".diff-line-old-content, .diff-line-new-content, .diff-line-old-num, .diff-line-new-num",
	);
	if (splitContent) {
		const splitRow = splitContent.closest("tr[data-side]");
		const splitLine = readPositiveLineNumber(
			splitRow
				?.querySelector("span[data-line-num]")
				?.getAttribute("data-line-num"),
		);
		if (splitLine) {
			return {
				lineNumber: splitLine,
				side: splitSideFromString(splitRow.getAttribute("data-side")),
			};
		}
	}

	const diffRow = target.closest("tr.diff-line[data-line]");
	if (!diffRow || diffRow.getAttribute("data-state") === "hunk") return null;

	const rowSide = diffRow.getAttribute("data-side");
	if (rowSide) {
		const splitLine = readPositiveLineNumber(
			diffRow
				.querySelector("span[data-line-num]")
				?.getAttribute("data-line-num"),
		);
		if (!splitLine) return null;
		return {
			lineNumber: splitLine,
			side: splitSideFromString(rowSide),
		};
	}

	const oldLine = readPositiveLineNumber(
		diffRow
			.querySelector("span[data-line-old-num]")
			?.getAttribute("data-line-old-num"),
	);
	const newLine = readPositiveLineNumber(
		diffRow
			.querySelector("span[data-line-new-num]")
			?.getAttribute("data-line-new-num"),
	);

	if (newLine) return { lineNumber: newLine, side: SplitSide.new };
	if (oldLine) return { lineNumber: oldLine, side: SplitSide.old };
	return null;
};

const isDiffLineNumberTarget = (target) =>
	target instanceof Element &&
	Boolean(
		target.closest(
			"span[data-line-new-num], span[data-line-old-num], span[data-line-num], .diff-line-old-num, .diff-line-new-num, .diff-line-num",
		),
	);

const isInteractiveDiffTarget = (target) =>
	target instanceof Element &&
	Boolean(
		target.closest(
			"button, a, input, select, textarea, [role='button'], [contenteditable='true']",
		),
	);

const hasNativeTextSelection = () => {
	const selection = window.getSelection?.();
	return Boolean(selection && !selection.isCollapsed && selection.toString());
};

const rangeIntersectsNode = (range, node) => {
	if (!range || !node) return false;
	try {
		return range.intersectsNode(node);
	} catch {
		return false;
	}
};

const getNativeSelectionTextForContextMenu = (target, container) => {
	const selection = window.getSelection?.();
	if (!selection || selection.isCollapsed || !selection.toString()) return "";
	if (!(container instanceof Element)) return "";

	const targetElement =
		target instanceof Element ? target : target?.parentElement || null;
	if (!targetElement || !container.contains(targetElement)) return "";

	const targetLineOrCell =
		targetElement.closest("tr.diff-line, td") || targetElement;
	for (let index = 0; index < selection.rangeCount; index += 1) {
		const range = selection.getRangeAt(index);
		if (
			rangeIntersectsNode(range, container) &&
			(rangeIntersectsNode(range, targetElement) ||
				rangeIntersectsNode(range, targetLineOrCell))
		) {
			return selection.toString();
		}
	}

	return "";
};

const getRowLineNumberForSide = (row, side) => {
	if (!(row instanceof Element)) return null;

	const rowSide = row.getAttribute("data-side");
	if (rowSide) {
		if (splitSideFromString(rowSide) !== side) return null;
		return readPositiveLineNumber(
			row.querySelector("span[data-line-num]")?.getAttribute("data-line-num"),
		);
	}

	const attribute =
		side === SplitSide.old ? "data-line-old-num" : "data-line-new-num";
	return readPositiveLineNumber(
		row.querySelector(`span[${attribute}]`)?.getAttribute(attribute),
	);
};

const findRenderedDiffLine = (section, side, lineNumber) => {
	if (!(section instanceof Element)) return null;
	const targetLine = readPositiveLineNumber(lineNumber);
	if (!targetLine) return null;

	return (
		[...section.querySelectorAll(".diff-line")].find((row) => {
			if (!(row instanceof Element)) return false;
			const state = row.getAttribute("data-state");
			if (state && state !== "diff" && state !== "plain") return false;
			return getRowLineNumberForSide(row, side) === targetLine;
		}) || null
	);
};

const parseDiffCodeLines = (file, side) => {
	const lines = [];
	let oldLine = 0;
	let newLine = 0;
	let inHunk = false;

	for (const rawLine of String(file?.diff || "")
		.replace(/\r\n?/g, "\n")
		.split("\n")) {
		const hunk = rawLine.match(/^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/);
		if (hunk) {
			oldLine = Number(hunk[1]);
			newLine = Number(hunk[2]);
			inHunk = true;
			continue;
		}

		if (!inHunk || rawLine.startsWith("\\ No newline")) continue;

		if (rawLine.startsWith("+")) {
			if (side === SplitSide.new) {
				lines.push({ lineNumber: newLine, text: rawLine.slice(1) });
			}
			newLine += 1;
			continue;
		}

		if (rawLine.startsWith("-")) {
			if (side === SplitSide.old) {
				lines.push({ lineNumber: oldLine, text: rawLine.slice(1) });
			}
			oldLine += 1;
			continue;
		}

		const text = rawLine.startsWith(" ") ? rawLine.slice(1) : rawLine;
		lines.push({
			lineNumber: side === SplitSide.old ? oldLine : newLine,
			text,
		});
		oldLine += 1;
		newLine += 1;
	}

	return lines;
};

const getSelectedCodeBlock = (file, selection) => {
	if (!selection || selection.fileId !== file?.id) return "";
	const bounds = selectedRangeBounds(selection);
	if (!bounds) return "";

	return parseDiffCodeLines(file, selection.side)
		.filter(
			(line) =>
				line.lineNumber >= bounds.start && line.lineNumber <= bounds.end,
		)
		.map((line) => line.text)
		.join("\n");
};

const getFormattedCodeBlock = (file, selection) => {
	const language = file?.lang && file.lang !== "txt" ? file.lang : "";
	return `\`\`\`${language}\n${getSelectedCodeBlock(file, selection)}\n\`\`\``;
};

const formatLineSelectionLabel = (file, selection) => {
	const bounds = selectedRangeBounds(selection);
	if (!bounds) return file?.path || "";
	return bounds.start === bounds.end
		? `${file.path}:${bounds.start}`
		: `${file.path}:${bounds.start}-${bounds.end}`;
};

const DiffFileSection = React.memo(
	({
		file,
		expanded,
		onToggle,
		mode,
		wrap,
		diffTheme,
		discussions,
		commentsVisible,
		canComment,
		createCodeComment,
		updateCodeComment,
		deleteCodeComment,
		replyCodeComment,
		addCodeCommentReaction,
		reactingEmojiKey,
		onCommentCreated,
		fullFileUrl,
		onOpenLineMenu,
		onSelectLine,
		selectedRange,
		scrollRootRef,
		loadFileContent,
		eagerRender = false,
	}) => {
		const sectionRef = useRef(null);
		const [shouldRenderDiff, setShouldRenderDiff] = useState(eagerRender);
		const status = fileStatus(file);
		const diffMode =
			mode === VIEW_MODE.UNIFIED
				? DiffModeEnum.Unified
				: DiffModeEnum.SplitGitLab;
		const commentExtendData = useMemo(
			() => buildCommentExtendData(file, discussions, commentsVisible),
			[file, discussions, commentsVisible],
		);
		const diffData = useMemo(
			() => ({
				oldFile: {
					fileName: file.oldPath,
					fileLang: file.lang,
					content: file.oldContent || "",
				},
				newFile: {
					fileName: file.newPath,
					fileLang: file.lang,
					content: file.newContent || "",
				},
				hunks: file.hunks,
			}),
			[
				file.hunks,
				file.lang,
				file.newContent,
				file.newPath,
				file.oldContent,
				file.oldPath,
			],
		);
		const fileSelection =
			selectedRange?.fileId === file.id ? selectedRange : null;
		const renderDiffBody = expanded && shouldRenderDiff;

		useEffect(() => {
			if (eagerRender) {
				setShouldRenderDiff(true);
			}
		}, [eagerRender]);

		useEffect(() => {
			if (
				!expanded ||
				!shouldRenderDiff ||
				file.rawContentLoaded ||
				file.rawContentError
			) {
				return;
			}
			loadFileContent?.(file);
		}, [expanded, file, loadFileContent, shouldRenderDiff]);

		useEffect(() => {
			if (!expanded || shouldRenderDiff) return undefined;
			const section = sectionRef.current;
			if (!section || typeof IntersectionObserver === "undefined") {
				setShouldRenderDiff(true);
				return undefined;
			}

			const observer = new IntersectionObserver(
				(entries) => {
					if (entries.some((entry) => entry.isIntersecting)) {
						setShouldRenderDiff(true);
						observer.disconnect();
					}
				},
				{
					root: scrollRootRef?.current || null,
					rootMargin: DIFF_LAZY_ROOT_MARGIN,
					threshold: 0,
				},
			);

			observer.observe(section);
			return () => observer.disconnect();
		}, [expanded, scrollRootRef, shouldRenderDiff]);

		useEffect(() => {
			const section = sectionRef.current;
			if (!section) return;

			const rows = [...section.querySelectorAll("tr.diff-line")];
			for (const row of rows) {
				row.classList.remove(
					"mr-diff-line-selected",
					"mr-diff-line-selected-start",
					"mr-diff-line-selected-end",
				);
			}

			const bounds = selectedRangeBounds(fileSelection);
			if (!bounds) return;

			for (const row of rows) {
				const lineNumber = getRowLineNumberForSide(row, fileSelection.side);
				if (
					!lineNumber ||
					lineNumber < bounds.start ||
					lineNumber > bounds.end
				) {
					continue;
				}

				row.classList.add("mr-diff-line-selected");
				if (lineNumber === bounds.start) {
					row.classList.add("mr-diff-line-selected-start");
				}
				if (lineNumber === bounds.end) {
					row.classList.add("mr-diff-line-selected-end");
				}
			}
		}, [fileSelection]);

		return (
			<section
				ref={sectionRef}
				id={fileElementId(file)}
				className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)]"
				onMouseDown={(event) => {
					if (event.button !== 0) return;
					if (isInteractiveDiffTarget(event.target)) return;
					const lineContext = getLineNumberContext(event.target);
					if (!lineContext) return;
					const shouldSelectLine =
						event.shiftKey || isDiffLineNumberTarget(event.target);
					if (!shouldSelectLine) return;

					event.preventDefault();
					window.getSelection()?.removeAllRanges();
					onSelectLine?.(file, lineContext, event);
				}}
				onMouseUp={(event) => {
					if (event.button !== 0) return;
					if (event.defaultPrevented) return;
					if (isInteractiveDiffTarget(event.target)) return;
					if (event.shiftKey || isDiffLineNumberTarget(event.target)) return;
					if (hasNativeTextSelection()) return;

					const lineContext = getLineNumberContext(event.target);
					if (!lineContext) return;
					onSelectLine?.(file, lineContext, event);
				}}
				onContextMenu={(event) => {
					const lineContext = getLineNumberContext(event.target);
					if (!lineContext) return;

					event.preventDefault();
					event.stopPropagation();
					onOpenLineMenu?.(event, file, lineContext);
				}}
			>
				<div className="sticky top-0 z-10 flex min-w-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-2">
					<button
						type="button"
						onClick={() => onToggle(file.id)}
						className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						aria-expanded={expanded}
						aria-label={expanded ? "Collapse file diff" : "Expand file diff"}
					>
						{expanded ? (
							<ChevronDown className="h-4 w-4" />
						) : (
							<ChevronRight className="h-4 w-4" />
						)}
					</button>
					<FileKindIcon
						path={file.path}
						className="h-4 w-4 shrink-0 text-[var(--color-text-tertiary)]"
					/>
					<div className="min-w-0 flex-1">
						<div className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
							{file.path}
						</div>
						{file.renamedFile && (
							<div className="truncate text-xs text-[var(--color-text-secondary)]">
								{file.oldPath} renamed to {file.newPath}
							</div>
						)}
					</div>
					<span
						className={`font-mono text-xs font-semibold ${status.className}`}
					>
						{status.label}
					</span>
					<span className="font-mono text-xs text-green-600 dark:text-green-400">
						+{file.additions}
					</span>
					<span className="font-mono text-xs text-red-600 dark:text-red-400">
						-{file.deletions}
					</span>
					{fullFileUrl && (
						<a
							href={fullFileUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
							aria-label="Open full file in GitLab"
							title="Open full file in GitLab"
						>
							<ExternalLink className="h-4 w-4" />
						</a>
					)}
				</div>
				{expanded && (
					<div className="overflow-x-auto">
						{!renderDiffBody ? (
							<div className="flex min-h-24 items-center justify-between gap-3 px-4 py-5 text-sm text-[var(--color-text-secondary)]">
								<span>Diff will load when scrolled into view.</span>
								<span className="shrink-0 font-mono text-xs">
									+{file.additions} -{file.deletions}
								</span>
							</div>
						) : file.hunks.length > 0 ? (
							<DiffView
								key={`${file.id}:${mode}:${wrap}:${diffTheme}:${file.rawContentLoaded ? "raw" : "patch"}`}
								data={diffData}
								diffViewMode={diffMode}
								diffViewTheme={diffTheme}
								diffViewHighlight={true}
								diffViewWrap={wrap}
								diffViewFontSize={13}
								diffViewAddWidget={canComment}
								extendData={commentExtendData}
								renderExtendLine={({ data }) => (
									<InlineCommentThread
										data={data}
										onUpdateComment={updateCodeComment}
										onDeleteComment={deleteCodeComment}
										onReplyComment={replyCodeComment}
										onReactComment={addCodeCommentReaction}
										reactingEmojiKey={reactingEmojiKey}
										onNotice={onCommentCreated}
									/>
								)}
								renderWidgetLine={({ lineNumber, side, onClose }) => (
									<DiffCommentWidget
										file={file}
										lineNumber={lineNumber}
										side={side}
										onClose={onClose}
										onCreateComment={createCodeComment}
										onCommentCreated={onCommentCreated}
									/>
								)}
							/>
						) : (
							<div className="px-4 py-6 text-sm text-[var(--color-text-secondary)]">
								Diff content is unavailable for this file.
							</div>
						)}
					</div>
				)}
			</section>
		);
	},
);

const DiffCommentsSection = ({
	mr,
	comments,
	height,
	onResizeStart,
	renderDiscussionReference,
	shortcut,
	ariaShortcut,
}) => {
	const displayedCommentCount = getDisplayedMRCommentCount(comments, mr);

	return (
		<section
			data-diff-comments-panel="true"
			className="diff-fast-motion pointer-events-auto absolute inset-x-0 bottom-0 z-40 flex max-h-full flex-col overflow-hidden border-t border-[var(--color-border)] bg-[var(--color-bg-primary)] shadow-[0_-18px_44px_rgba(0,0,0,0.24)]"
			style={comments.expanded ? { height } : undefined}
		>
			{comments.expanded && (
				<button
					type="button"
					onMouseDown={onResizeStart}
					className="group absolute inset-x-0 top-0 z-10 flex h-2 cursor-row-resize items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
					aria-label="Resize comments panel"
					title="Drag to resize comments panel"
				>
					<span className="h-0.5 w-10 rounded-full bg-[var(--color-border)] opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
				</button>
			)}
			<div className="flex min-w-0 items-center gap-2 px-3 py-2">
				<button
					type="button"
					onClick={comments.toggle}
					className="flex min-w-0 flex-1 items-center gap-2 rounded-md text-left text-[var(--color-text-primary)] hover:text-[var(--color-link)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
					aria-expanded={comments.expanded}
					aria-keyshortcuts={ariaShortcut}
					title={
						comments.expanded
							? `Hide comments (${shortcut})`
							: `Show comments (${shortcut})`
					}
				>
					<span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--color-text-secondary)]">
						{comments.expanded ? (
							<ChevronDown className="h-4 w-4" />
						) : (
							<ChevronRight className="h-4 w-4" />
						)}
					</span>
					<MessageSquare className="h-4 w-4 shrink-0 text-[var(--color-text-tertiary)]" />
					<span className="truncate text-sm font-semibold">Comments</span>
					<span className="shrink-0 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-[var(--color-text-secondary)]">
						{displayedCommentCount > 99 ? "99+" : displayedCommentCount}
					</span>
					{shortcut && (
						<span className="hidden shrink-0 rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-1 py-0.5 font-mono text-[10px] leading-none text-[var(--color-text-secondary)] sm:inline-flex">
							{shortcut}
						</span>
					)}
				</button>
				{comments.expanded && (
					<button
						type="button"
						onClick={comments.openModal}
						className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2 text-xs font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
					>
						<ExternalLink className="h-3.5 w-3.5" />
						Open all
					</button>
				)}
			</div>
			{comments.expanded && (
				<div className="min-h-0 flex-1 border-t border-[var(--color-border)] bg-[var(--color-bg-secondary)]/35">
					<div className="h-full overflow-y-auto overscroll-contain px-3 py-3">
						<MRCommentsThreadedPanel
							comments={comments}
							compact
							renderDiscussionReference={renderDiscussionReference}
						/>
					</div>
				</div>
			)}
		</section>
	);
};

export const MRDiffViewer = ({ mr, onClose }) => {
	const { theme } = useTheme();
	const appDiffTheme = theme === THEMES.LINEAR_LIGHT ? "light" : "dark";
	const [syntaxThemePreference, setSyntaxThemePreference] = useLocalStorage(
		DIFF_SYNTAX_THEME_STORAGE_KEY,
		DIFF_SYNTAX_THEME_AUTO,
	);
	const selectedSyntaxTheme = getDiffSyntaxThemeOption(syntaxThemePreference);
	const resolvedSyntaxTheme =
		selectedSyntaxTheme.value === DIFF_SYNTAX_THEME_AUTO
			? getDiffSyntaxThemeOption(
					appDiffTheme === "light" ? "github-light" : "github-dark",
				)
			: selectedSyntaxTheme;
	const diffTheme = resolvedSyntaxTheme.diffTheme || appDiffTheme;
	const [isVisible, setIsVisible] = useState(false);
	const closeTimerRef = useRef(null);
	const [mode, setMode] = useLocalStorage(
		"gitlab-diff-view-mode",
		VIEW_MODE.SPLIT,
	);
	const [wrap, setWrap] = useLocalStorage("gitlab-diff-view-wrap", false);
	const [sidebarOpen, setSidebarOpen] = useLocalStorage(
		"gitlab-diff-view-sidebar-open",
		true,
	);
	const [presentationMode, setStoredPresentationMode] = useLocalStorage(
		DIFF_VIEWER_PRESENTATION_STORAGE_KEY,
		PRESENTATION_MODE.FULLSCREEN,
	);
	const [, setPresentationModeSelected] = useLocalStorage(
		DIFF_VIEWER_PRESENTATION_SELECTED_STORAGE_KEY,
		false,
	);
	const resolvedPresentationMode = presentationMode;
	const setPresentationMode = useCallback(
		(nextMode) => {
			setPresentationModeSelected(true);
			setStoredPresentationMode((currentMode) => {
				const resolvedMode =
					typeof nextMode === "function" ? nextMode(currentMode) : nextMode;
				return resolvedMode === PRESENTATION_MODE.FULLSCREEN
					? PRESENTATION_MODE.FULLSCREEN
					: PRESENTATION_MODE.MODAL;
			});
		},
		[setPresentationModeSelected, setStoredPresentationMode],
	);
	const {
		files,
		discussions,
		loading,
		commentsLoading,
		commentsError,
		error,
		loaded,
		projectPath,
		diffRefs,
		approvalState,
		approvalAction,
		reload,
		reloadComments,
		reloadApprovalState,
		loadFileContent,
		canComment,
		createCodeComment,
		updateCodeComment,
		deleteCodeComment,
		replyCodeComment,
		addCodeCommentReaction,
		reactingEmojiKey,
		approveMergeRequest,
		rejectMergeRequest,
	} = useMergeRequestDiff(mr, Boolean(mr));
	const diffComments = useMRComments(mr);
	const [commentsVisible, setCommentsVisible] = useLocalStorage(
		"gitlab-diff-view-comments-visible",
		true,
	);
	const [storedCommentsPanelHeight, setStoredCommentsPanelHeight] =
		useLocalStorage(
			"gitlab-diff-view-comments-panel-height",
			DIFF_COMMENTS_PANEL_DEFAULT_HEIGHT,
		);
	const [expandedDirs, setExpandedDirs] = useState({});
	const [expandedFiles, setExpandedFiles] = useState({});
	const [activeFileId, setActiveFileId] = useState(null);
	const [sidebarFilter, setSidebarFilter] = useState("");
	const [fileSearchOpen, setFileSearchOpen] = useState(false);
	const [diffSearchOpen, setDiffSearchOpen] = useState(false);
	const [diffSearchQuery, setDiffSearchQuery] = useState("");
	const [diffSearchCaseInsensitive, setDiffSearchCaseInsensitive] =
		useLocalStorage("gitlab-diff-view-search-case-insensitive", true);
	const [diffSearchActiveIndex, setDiffSearchActiveIndex] = useState(0);
	const [pathMenu, setPathMenu] = useState(null);
	const [lineMenu, setLineMenu] = useState(null);
	const [notice, setNotice] = useState(null);
	const [selectedRange, setSelectedRange] = useState(null);
	const [mrLinkCopied, setMrLinkCopied] = useState(false);
	const [liveCommentsPanelHeight, setLiveCommentsPanelHeight] = useState(null);
	const mainRef = useRef(null);
	const sidebarListRef = useRef(null);
	const diffSearchInputRef = useRef(null);
	const activeFileIdRef = useRef(null);
	const scrollFrameRef = useRef(null);
	const commentJumpTimerRef = useRef(null);
	const lineJumpTimerRef = useRef(null);
	const commentsResizeRef = useRef(null);
	const lastWrittenFilePathRef = useRef(readActiveFilePathFromLocation());
	const fileTree = useMemo(() => buildFileTree(files), [files]);
	const dirPaths = useMemo(() => collectDirPaths(fileTree), [fileTree]);
	const filteredFileTree = useMemo(
		() => filterFileTree(fileTree, sidebarFilter) || buildFileTree([]),
		[fileTree, sidebarFilter],
	);
	const filteredFileCount = useMemo(
		() => countFilesInTree(filteredFileTree),
		[filteredFileTree],
	);
	const diffSearchResults = useMemo(
		() =>
			collectDiffSearchMatches(
				files,
				diffSearchQuery,
				diffSearchCaseInsensitive,
			),
		[diffSearchCaseInsensitive, diffSearchQuery, files],
	);
	const diffSearchDisplayIndex =
		diffSearchResults.length === 0
			? 0
			: Math.min(diffSearchActiveIndex, diffSearchResults.length - 1);
	const activeDiffSearchResult =
		diffSearchResults[diffSearchDisplayIndex] || null;
	const activeFile = files.find((file) => file.id === activeFileId) || files[0];
	const totalAdditions = files.reduce((sum, file) => sum + file.additions, 0);
	const totalDeletions = files.reduce((sum, file) => sum + file.deletions, 0);
	const commentCount = countDiscussionNotes(discussions);
	const codeReferencesByDiscussionId = useMemo(
		() => buildDiscussionCodeReferences(files, discussions),
		[files, discussions],
	);
	const safeMrUrl = safeHttpUrl(mr?.webUrl);
	const activeFileUrl = activeFile
		? buildGitLabFileUrl({ projectPath, diffRefs, file: activeFile })
		: "";
	const allDirsExpanded =
		dirPaths.length > 0 && dirPaths.every((path) => expandedDirs[path] ?? true);
	const toggleAllDirsLabel = allDirsExpanded
		? "Collapse all folders"
		: "Expand all folders";
	const currentUserApproved = Boolean(approvalState?.currentUserApproved);
	const approvalBusy = Boolean(approvalAction);
	const approvalTooltip =
		typeof approvalState?.approvalsLeft === "number"
			? `${approvalState.approvalsLeft} approval${approvalState.approvalsLeft === 1 ? "" : "s"} left`
			: "Approve merge request";
	const isFullscreen = resolvedPresentationMode !== PRESENTATION_MODE.MODAL;
	const noticeIsError = Boolean(notice && /unable|failed|error/i.test(notice));
	const closeFileSearch = useCallback(() => setFileSearchOpen(false), []);
	const openDiffSearch = useCallback(() => {
		setDiffSearchOpen(true);
		setFileSearchOpen(false);
		window.requestAnimationFrame(() => {
			diffSearchInputRef.current?.focus();
			diffSearchInputRef.current?.select();
		});
	}, []);
	const closeDiffSearch = useCallback(() => setDiffSearchOpen(false), []);
	const updateDiffSearchQuery = useCallback((query) => {
		setDiffSearchQuery(query);
		setDiffSearchActiveIndex(0);
	}, []);
	const updateDiffSearchCaseInsensitive = useCallback(
		(caseInsensitive) => {
			setDiffSearchCaseInsensitive(caseInsensitive);
			setDiffSearchActiveIndex(0);
		},
		[setDiffSearchCaseInsensitive],
	);
	const closeViewer = useCallback(() => {
		setIsVisible(false);
		window.clearTimeout(closeTimerRef.current);
		closeTimerRef.current = window.setTimeout(onClose, 180);
	}, [onClose]);
	const diffCommentsCount = getDisplayedMRCommentCount(diffComments, mr);
	const diffCommentsExpanded = diffComments.expanded;
	const setDiffCommentsExpanded = diffComments.setExpanded;
	const diffCommentsModalEntry = diffComments.modalEntry;
	const diffCommentsModalOpen = diffComments.modalOpen;
	const closeDiffCommentsModal = diffComments.closeModal;
	const setDiffCommentsModalEntry = diffComments.setModalEntry;
	const diffCommentsShortcut = useMemo(getDiffCommentsShortcut, []);
	const diffCommentsAriaShortcut = useMemo(getDiffCommentsAriaShortcut, []);
	const clampCommentsPanelHeight = useCallback((height) => {
		const containerHeight =
			mainRef.current?.parentElement?.clientHeight ||
			window.innerHeight ||
			DIFF_COMMENTS_PANEL_MAX_HEIGHT;
		const maxHeight = Math.max(
			DIFF_COMMENTS_PANEL_MIN_HEIGHT,
			Math.min(DIFF_COMMENTS_PANEL_MAX_HEIGHT, containerHeight - 72),
		);
		const numericHeight = Number(height) || DIFF_COMMENTS_PANEL_DEFAULT_HEIGHT;
		return clampNumber(
			Math.round(numericHeight),
			DIFF_COMMENTS_PANEL_MIN_HEIGHT,
			maxHeight,
		);
	}, []);
	const commentsPanelHeight = clampCommentsPanelHeight(
		liveCommentsPanelHeight ?? storedCommentsPanelHeight,
	);
	const startCommentsResize = useCallback(
		(event) => {
			if (!diffCommentsExpanded) return;

			event.preventDefault();
			event.stopPropagation();

			const startY = event.clientY;
			const startHeight = commentsPanelHeight;
			document.body.style.userSelect = "none";
			document.body.style.cursor = "row-resize";

			const handleMove = (moveEvent) => {
				const nextHeight = startHeight + startY - moveEvent.clientY;
				setLiveCommentsPanelHeight(clampCommentsPanelHeight(nextHeight));
			};

			let cleanup = () => {};
			const handleUp = (upEvent) => {
				const nextHeight = startHeight + startY - upEvent.clientY;
				setStoredCommentsPanelHeight(clampCommentsPanelHeight(nextHeight));
				setLiveCommentsPanelHeight(null);
				cleanup();
			};

			cleanup = () => {
				document.body.style.userSelect = "";
				document.body.style.cursor = "";
				window.removeEventListener("mousemove", handleMove);
				window.removeEventListener("mouseup", handleUp);
				commentsResizeRef.current = null;
			};

			commentsResizeRef.current = { cleanup };
			window.addEventListener("mousemove", handleMove);
			window.addEventListener("mouseup", handleUp);
		},
		[
			clampCommentsPanelHeight,
			commentsPanelHeight,
			diffCommentsExpanded,
			setStoredCommentsPanelHeight,
		],
	);

	useEffect(() => {
		return () => {
			if (commentsResizeRef.current?.cleanup) {
				commentsResizeRef.current.cleanup();
			}
		};
	}, []);

	const copyPath = async (path) => {
		const ok = await copyText(path);
		setNotice(ok ? `Copied ${path}` : "Unable to copy path");
	};

	const copyGitLabPathLink = async (menu) => {
		const url = buildGitLabPathUrl({ projectPath, diffRefs, menu });
		const ok = await copyText(url);
		setNotice(
			ok ? `Copied GitLab link for ${menu.path}` : "Unable to copy GitLab link",
		);
	};

	const copyMergeRequestLink = async () => {
		const ok = await copyText(safeMrUrl);
		setMrLinkCopied(ok);
		setNotice(
			ok ? "Copied merge request link" : "Unable to copy merge request link",
		);
		if (ok) {
			window.setTimeout(() => setMrLinkCopied(false), 1400);
		}
	};

	const toggleDiffCommentsSection = useCallback(() => {
		setDiffCommentsExpanded((expanded) => !expanded);
	}, [setDiffCommentsExpanded]);

	const selectDiffLine = useCallback((file, { lineNumber, side }, event) => {
		if (event?.shiftKey) {
			window.getSelection()?.removeAllRanges();
			window.requestAnimationFrame(() => {
				window.getSelection()?.removeAllRanges();
			});
		}

		setSelectedRange((currentSelection) => {
			if (
				event?.shiftKey &&
				currentSelection?.fileId === file.id &&
				currentSelection.side === side
			) {
				const anchorLine =
					currentSelection.anchorLine ?? currentSelection.startLine;
				return {
					fileId: file.id,
					side,
					anchorLine,
					startLine: Math.min(anchorLine, lineNumber),
					endLine: Math.max(anchorLine, lineNumber),
				};
			}

			return {
				fileId: file.id,
				side,
				anchorLine: lineNumber,
				startLine: lineNumber,
				endLine: lineNumber,
			};
		});
	}, []);

	const openLineMenu = useCallback(
		(event, file, lineContext) => {
			event.preventDefault();
			event.stopPropagation();
			if (!file || !lineContext?.lineNumber) return;

			const selectedText = getNativeSelectionTextForContextMenu(
				event.target,
				event.currentTarget,
			);
			const nextSelection = selectionContainsLine(
				selectedRange,
				file,
				lineContext,
			)
				? selectedRange
				: lineSelectionFromContext(file, lineContext);

			if (!selectedText) {
				window.getSelection()?.removeAllRanges();
				setSelectedRange(nextSelection);
			}
			setPathMenu(null);
			setLineMenu({
				file,
				lineContext,
				selection: nextSelection,
				selectedText,
				x: Math.min(event.clientX, window.innerWidth - 224),
				y: Math.min(event.clientY, window.innerHeight - 104),
			});
		},
		[selectedRange],
	);

	const copyFormattedLineSelection = useCallback(async (menu) => {
		if (menu.selectedText) {
			const ok = await copyText(menu.selectedText);
			setNotice(ok ? "Copied selected text" : "Unable to copy selected text");
			return;
		}

		const selection =
			menu.selection || lineSelectionFromContext(menu.file, menu.lineContext);
		const formattedCode = getFormattedCodeBlock(menu.file, selection);
		const ok = await copyText(formattedCode);
		setNotice(
			ok
				? `Copied formatted code for ${formatLineSelectionLabel(menu.file, selection)}`
				: "Unable to copy formatted code",
		);
	}, []);

	const copyGitLabLineSelection = useCallback(
		async (menu) => {
			const selection =
				menu.selection || lineSelectionFromContext(menu.file, menu.lineContext);
			const bounds = selectedRangeBounds(selection);
			const url = buildGitLabLineUrl({
				projectPath,
				diffRefs,
				file: menu.file,
				startLine: bounds?.start,
				endLine: bounds?.end,
				side: selection.side,
			});
			const ok = await copyText(url);
			setNotice(
				ok
					? `Copied GitLab link for ${formatLineSelectionLabel(menu.file, selection)}`
					: "Unable to copy GitLab line link",
			);
		},
		[diffRefs, projectPath],
	);

	const scrollToVisibleComment = useCallback(
		(direction) => {
			const scrollContainer = mainRef.current;
			if (!scrollContainer) return;

			const commentElements = [
				...scrollContainer.querySelectorAll(COMMENT_THREAD_SELECTOR),
			].filter((element) => element instanceof HTMLElement);

			if (commentElements.length === 0) {
				setNotice(
					commentCount > 0
						? "Comments are still loading"
						: "No inline comments to navigate",
				);
				return;
			}

			const containerRect = scrollContainer.getBoundingClientRect();
			const currentTop = containerRect.top + 56;
			const target =
				direction === "previous"
					? [...commentElements]
							.reverse()
							.find(
								(element) =>
									element.getBoundingClientRect().top < currentTop - 2,
							) || commentElements[commentElements.length - 1]
					: commentElements.find(
							(element) => element.getBoundingClientRect().top > currentTop + 2,
						) || commentElements[0];
			const targetRect = target.getBoundingClientRect();

			scrollContainer.scrollTo({
				top:
					scrollContainer.scrollTop + targetRect.top - containerRect.top - 56,
				behavior: "smooth",
			});

			target.focus({ preventScroll: true });
			for (const element of commentElements) {
				element.classList.remove("mr-diff-comment-target");
			}
			target.classList.add("mr-diff-comment-target");
			window.clearTimeout(commentJumpTimerRef.current);
			commentJumpTimerRef.current = window.setTimeout(() => {
				target.classList.remove("mr-diff-comment-target");
			}, 1600);

			const targetFileId = target.getAttribute("data-file-id");
			const targetFilePath = target.getAttribute("data-file-path");
			if (targetFileId) {
				activeFileIdRef.current = targetFileId;
				setActiveFileId(targetFileId);
			}
			if (targetFilePath && lastWrittenFilePathRef.current !== targetFilePath) {
				writeActiveFilePathToHistory(targetFilePath);
				lastWrittenFilePathRef.current = targetFilePath;
			}
		},
		[commentCount],
	);

	const jumpToComment = useCallback(
		(direction) => {
			if (!commentsVisible) {
				setCommentsVisible(true);
				window.requestAnimationFrame(() => {
					window.requestAnimationFrame(() => scrollToVisibleComment(direction));
				});
				return;
			}

			scrollToVisibleComment(direction);
		},
		[commentsVisible, scrollToVisibleComment, setCommentsVisible],
	);

	const reloadDiffAndComments = async () => {
		await reload();
		await reloadApprovalState({ silent: true });
	};

	const approveMR = async () => {
		try {
			await approveMergeRequest();
			setNotice("Approved merge request");
		} catch (error) {
			setNotice(error.message || "Unable to approve merge request");
		}
	};

	const rejectMR = async () => {
		try {
			await rejectMergeRequest();
			setNotice("Removed your approval");
		} catch (error) {
			setNotice(error.message || "Unable to reject merge request");
		}
	};

	const scrollFileIntoView = useCallback((file, behavior = "auto") => {
		if (!file) return;
		const element = document.getElementById(fileElementId(file));
		if (!element) return;

		element.scrollIntoView({
			behavior,
			block: "start",
			inline: "nearest",
		});
	}, []);

	const selectFile = useCallback(
		(file, { updateUrl = true, scroll = true } = {}) => {
			if (!file) return;

			setExpandedFiles((prev) => ({ ...prev, [file.id]: true }));
			setExpandedDirs((prev) => ({
				...prev,
				...Object.fromEntries(
					getAncestorDirPaths(file.path).map((path) => [path, true]),
				),
			}));
			setActiveFileId(file.id);
			activeFileIdRef.current = file.id;
			setFileSearchOpen(false);

			if (updateUrl && lastWrittenFilePathRef.current !== file.path) {
				writeActiveFilePathToHistory(file.path);
				lastWrittenFilePathRef.current = file.path;
			}

			if (scroll) {
				window.requestAnimationFrame(() => scrollFileIntoView(file, "auto"));
			}
		},
		[scrollFileIntoView],
	);

	const jumpToFile = useCallback(
		(direction) => {
			if (files.length === 0) {
				setNotice("No files to navigate");
				return;
			}
			if (files.length === 1) {
				setNotice("Only one file in this diff");
				return;
			}

			const activeIndex = files.findIndex(
				(file) => file.id === activeFileIdRef.current,
			);
			const currentIndex = activeIndex >= 0 ? activeIndex : 0;
			const offset = direction === "previous" ? -1 : 1;
			const nextIndex = (currentIndex + offset + files.length) % files.length;
			const nextFile = files[nextIndex];
			if (!nextFile) return;

			selectFile(nextFile, { scroll: false });
			window.requestAnimationFrame(() =>
				scrollFileIntoView(nextFile, "smooth"),
			);
		},
		[files, scrollFileIntoView, selectFile],
	);

	const jumpToDiffSearchResult = useCallback(
		(result, behavior = "smooth") => {
			const file = result?.file;
			if (!file) return;

			selectFile(file, { scroll: false });

			setSelectedRange({
				fileId: file.id,
				side: result.side,
				anchorLine: result.lineNumber,
				startLine: result.lineNumber,
				endLine: result.lineNumber,
			});

			const findAndScroll = (attempt = 0) => {
				const section = document.getElementById(fileElementId(file));
				if (section && attempt === 0) {
					scrollFileIntoView(file, behavior);
				}

				const target =
					findRenderedDiffLine(section, result.side, result.lineNumber) ||
					(result.oldLine
						? findRenderedDiffLine(section, SplitSide.old, result.oldLine)
						: null) ||
					(result.newLine
						? findRenderedDiffLine(section, SplitSide.new, result.newLine)
						: null);

				if (!target) {
					if (attempt < 16) {
						window.setTimeout(() => findAndScroll(attempt + 1), 60);
					}
					return;
				}

				const scrollContainer = mainRef.current;
				if (scrollContainer instanceof HTMLElement) {
					const containerRect = scrollContainer.getBoundingClientRect();
					const targetRect = target.getBoundingClientRect();
					scrollContainer.scrollTo({
						top:
							scrollContainer.scrollTop +
							targetRect.top -
							containerRect.top -
							96,
						behavior,
					});
				} else {
					target.scrollIntoView({
						behavior,
						block: "center",
						inline: "nearest",
					});
				}

				for (const element of document.querySelectorAll(
					".mr-diff-line-jump-target",
				)) {
					element.classList.remove("mr-diff-line-jump-target");
				}
				target.classList.add("mr-diff-line-jump-target");
				window.clearTimeout(lineJumpTimerRef.current);
				lineJumpTimerRef.current = window.setTimeout(() => {
					target.classList.remove("mr-diff-line-jump-target");
				}, LINE_JUMP_HIGHLIGHT_MS);
			};

			window.requestAnimationFrame(() => findAndScroll());
		},
		[scrollFileIntoView, selectFile],
	);

	const jumpToDiffSearchMatch = useCallback(
		(direction) => {
			if (diffSearchResults.length === 0) return;

			setDiffSearchActiveIndex((currentIndex) => {
				const offset = direction === "previous" ? -1 : 1;
				const nextIndex =
					(currentIndex + offset + diffSearchResults.length) %
					diffSearchResults.length;

				if (nextIndex === currentIndex) {
					window.requestAnimationFrame(() =>
						jumpToDiffSearchResult(diffSearchResults[nextIndex]),
					);
				}

				return nextIndex;
			});
		},
		[diffSearchResults, jumpToDiffSearchResult],
	);

	const jumpToDiffReference = useCallback(
		(reference) => {
			const targetLine = readPositiveLineNumber(reference?.lineNumber);
			const file =
				files.find((candidate) => candidate.id === reference?.fileId) ||
				findFileByPath(files, reference?.filePath);
			if (!file || !targetLine) {
				setNotice("Referenced diff line is unavailable");
				return;
			}

			const side = splitSideFromString(reference.side);
			setExpandedFiles((prev) => ({ ...prev, [file.id]: true }));
			setExpandedDirs((prev) => ({
				...prev,
				...Object.fromEntries(
					getAncestorDirPaths(file.path).map((path) => [path, true]),
				),
			}));
			setActiveFileId(file.id);
			activeFileIdRef.current = file.id;
			setSelectedRange({
				fileId: file.id,
				side,
				anchorLine: targetLine,
				startLine: targetLine,
				endLine: targetLine,
			});

			if (lastWrittenFilePathRef.current !== file.path) {
				writeActiveFilePathToHistory(file.path);
				lastWrittenFilePathRef.current = file.path;
			}

			const findAndScroll = (attempt = 0) => {
				const section = document.getElementById(fileElementId(file));
				if (section) {
					scrollFileIntoView(file, attempt === 0 ? "smooth" : "auto");
				}

				const target = findRenderedDiffLine(section, side, targetLine);
				if (!target) {
					if (attempt < 16) {
						window.setTimeout(() => findAndScroll(attempt + 1), 60);
						return;
					}
					setNotice("Referenced diff line is not rendered yet");
					return;
				}

				const scrollContainer = mainRef.current;
				if (scrollContainer instanceof HTMLElement) {
					const containerRect = scrollContainer.getBoundingClientRect();
					const targetRect = target.getBoundingClientRect();
					scrollContainer.scrollTo({
						top:
							scrollContainer.scrollTop +
							targetRect.top -
							containerRect.top -
							88,
						behavior: "smooth",
					});
				} else {
					target.scrollIntoView({
						behavior: "smooth",
						block: "center",
						inline: "nearest",
					});
				}

				if (target instanceof HTMLElement) {
					target.tabIndex = -1;
					target.focus({ preventScroll: true });
				}

				for (const element of document.querySelectorAll(
					".mr-diff-line-jump-target",
				)) {
					element.classList.remove("mr-diff-line-jump-target");
				}
				target.classList.add("mr-diff-line-jump-target");
				window.clearTimeout(lineJumpTimerRef.current);
				lineJumpTimerRef.current = window.setTimeout(() => {
					target.classList.remove("mr-diff-line-jump-target");
				}, LINE_JUMP_HIGHLIGHT_MS);
			};

			window.requestAnimationFrame(() => findAndScroll());
		},
		[files, scrollFileIntoView],
	);

	const renderDiscussionReference = useCallback(
		(discussion) => {
			const reference = codeReferencesByDiscussionId.get(discussion.id);
			if (!reference) return null;

			return (
				<ReferencedCodeSnippet
					reference={reference}
					onJumpToDiff={() => jumpToDiffReference(reference)}
				/>
			);
		},
		[codeReferencesByDiscussionId, jumpToDiffReference],
	);

	const openPathMenu = (event, node) => {
		event.preventDefault();
		event.stopPropagation();
		if (!node?.path) return;

		setPathMenu({
			path: node.path,
			type: node.type,
			file: node.type === "file" ? node.file : null,
			x: Math.min(event.clientX, window.innerWidth - 208),
			y: Math.min(event.clientY, window.innerHeight - 56),
		});
		setLineMenu(null);
	};

	useEffect(() => {
		activeFileIdRef.current = activeFileId;
	}, [activeFileId]);

	useEffect(() => {
		const hasSidebarFilter = sidebarFilter.trim().length > 0;
		if (
			!sidebarOpen ||
			!activeFileId ||
			(hasSidebarFilter && filteredFileCount === 0)
		) {
			return undefined;
		}

		const frameId = window.requestAnimationFrame(() => {
			const container = sidebarListRef.current;
			if (!container) return;

			const activeRow = [
				...container.querySelectorAll("[data-diff-file-nav-id]"),
			].find(
				(element) =>
					element instanceof HTMLElement &&
					element.dataset.diffFileNavId === activeFileId,
			);
			if (!activeRow) return;

			const containerRect = container.getBoundingClientRect();
			const rowRect = activeRow.getBoundingClientRect();
			const margin = 16;

			if (rowRect.top < containerRect.top + margin) {
				container.scrollTo({
					top: container.scrollTop + rowRect.top - containerRect.top - margin,
					behavior: "auto",
				});
				return;
			}

			if (rowRect.bottom > containerRect.bottom - margin) {
				container.scrollTo({
					top:
						container.scrollTop +
						rowRect.bottom -
						containerRect.bottom +
						margin,
					behavior: "auto",
				});
			}
		});

		return () => window.cancelAnimationFrame(frameId);
	}, [activeFileId, sidebarOpen, sidebarFilter, filteredFileCount]);

	useEffect(() => {
		const frameId = window.requestAnimationFrame(() => setIsVisible(true));
		return () => {
			window.cancelAnimationFrame(frameId);
			window.clearTimeout(closeTimerRef.current);
			window.clearTimeout(commentJumpTimerRef.current);
			window.clearTimeout(lineJumpTimerRef.current);
		};
	}, []);

	useEffect(() => {
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, []);

	useEffect(() => {
		if (!pathMenu) return undefined;

		const closeMenu = () => setPathMenu(null);
		const handleKeyDown = (event) => {
			if (event.key === "Escape") closeMenu();
		};

		window.addEventListener("mousedown", closeMenu);
		window.addEventListener("keydown", handleKeyDown);
		window.addEventListener("scroll", closeMenu, true);

		return () => {
			window.removeEventListener("mousedown", closeMenu);
			window.removeEventListener("keydown", handleKeyDown);
			window.removeEventListener("scroll", closeMenu, true);
		};
	}, [pathMenu]);

	useEffect(() => {
		if (!lineMenu) return undefined;

		const closeMenu = () => setLineMenu(null);
		const handleKeyDown = (event) => {
			if (event.key === "Escape") closeMenu();
		};

		window.addEventListener("mousedown", closeMenu);
		window.addEventListener("keydown", handleKeyDown);
		window.addEventListener("scroll", closeMenu, true);

		return () => {
			window.removeEventListener("mousedown", closeMenu);
			window.removeEventListener("keydown", handleKeyDown);
			window.removeEventListener("scroll", closeMenu, true);
		};
	}, [lineMenu]);

	useEffect(() => {
		setDiffSearchActiveIndex((index) => {
			if (diffSearchResults.length === 0) return 0;
			return Math.min(index, diffSearchResults.length - 1);
		});
	}, [diffSearchResults.length]);

	useEffect(() => {
		if (
			!diffSearchOpen ||
			!normalizeSearchQuery(diffSearchQuery) ||
			!activeDiffSearchResult
		) {
			return undefined;
		}

		const frameId = window.requestAnimationFrame(() => {
			jumpToDiffSearchResult(activeDiffSearchResult);
		});

		return () => window.cancelAnimationFrame(frameId);
	}, [
		activeDiffSearchResult,
		diffSearchOpen,
		diffSearchQuery,
		jumpToDiffSearchResult,
	]);

	useEffect(() => {
		const handleFindShortcut = (event) => {
			if (
				!(event.metaKey || event.ctrlKey) ||
				String(event.key).toLowerCase() !== "f"
			) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			openDiffSearch();
		};

		window.addEventListener("keydown", handleFindShortcut, true);
		return () =>
			window.removeEventListener("keydown", handleFindShortcut, true);
	}, [openDiffSearch]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "p") {
				event.preventDefault();
				setFileSearchOpen(true);
				return;
			}

			if (
				isDiffCommentsShortcutEvent(event) &&
				!fileSearchOpen &&
				!isEditableKeyboardTarget(event.target)
			) {
				event.preventDefault();
				toggleDiffCommentsSection();
				return;
			}

			if (
				(event.metaKey || event.ctrlKey) &&
				!fileSearchOpen &&
				!isEditableKeyboardTarget(event.target) &&
				event.shiftKey &&
				(event.key === "ArrowDown" || event.key === "ArrowUp")
			) {
				event.preventDefault();
				jumpToFile(event.key === "ArrowDown" ? "next" : "previous");
				return;
			}

			if (
				(event.metaKey || event.ctrlKey) &&
				!fileSearchOpen &&
				!isEditableKeyboardTarget(event.target) &&
				!event.shiftKey &&
				(event.key === "ArrowDown" || event.key === "ArrowUp")
			) {
				event.preventDefault();
				jumpToComment(event.key === "ArrowDown" ? "next" : "previous");
				return;
			}

			if (fileSearchOpen) return;

			if (event.key === "Escape") {
				if (diffSearchOpen) {
					event.preventDefault();
					closeDiffSearch();
					return;
				}

				if (
					event.target instanceof Element &&
					event.target.closest("[data-diff-comment-editor='true']")
				) {
					consumeEscapeKey(event);
					return;
				}

				if (diffCommentsModalEntry) {
					consumeEscapeKey(event);
					setDiffCommentsModalEntry(null);
					return;
				}

				if (diffCommentsModalOpen) {
					consumeEscapeKey(event);
					closeDiffCommentsModal();
					return;
				}

				if (diffCommentsExpanded) {
					consumeEscapeKey(event);
					setDiffCommentsExpanded(false);
					return;
				}

				closeViewer();
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [
		closeDiffSearch,
		closeViewer,
		diffCommentsExpanded,
		diffCommentsModalEntry,
		diffCommentsModalOpen,
		diffSearchOpen,
		fileSearchOpen,
		jumpToComment,
		jumpToFile,
		closeDiffCommentsModal,
		setDiffCommentsExpanded,
		setDiffCommentsModalEntry,
		toggleDiffCommentsSection,
	]);

	useEffect(() => {
		setExpandedDirs(Object.fromEntries(dirPaths.map((path) => [path, true])));
	}, [dirPaths]);

	useEffect(() => {
		if (files.length === 0) {
			setExpandedFiles({});
			setActiveFileId(null);
			activeFileIdRef.current = null;
			setSelectedRange(null);
			return;
		}

		setExpandedFiles(Object.fromEntries(files.map((file) => [file.id, true])));

		const requestedFile = findFileByPath(
			files,
			readActiveFilePathFromLocation(),
		);
		const currentFile =
			files.find((file) => file.id === activeFileIdRef.current) || null;
		const nextFile = requestedFile || currentFile || files[0];

		setActiveFileId(nextFile.id);
		activeFileIdRef.current = nextFile.id;

		if (lastWrittenFilePathRef.current !== nextFile.path) {
			writeActiveFilePathToHistory(nextFile.path);
			lastWrittenFilePathRef.current = nextFile.path;
		}

		window.requestAnimationFrame(() => scrollFileIntoView(nextFile, "auto"));
		setSelectedRange((selection) =>
			selection && files.some((file) => file.id === selection.fileId)
				? selection
				: null,
		);
	}, [files, scrollFileIntoView]);

	useEffect(() => {
		const handlePopState = () => {
			const requestedFile = findFileByPath(
				files,
				readActiveFilePathFromLocation(),
			);
			if (requestedFile) {
				selectFile(requestedFile, { updateUrl: false });
			}
		};

		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
	}, [files, selectFile]);

	useEffect(() => {
		const scrollContainer = mainRef.current;
		if (!scrollContainer || files.length === 0) return undefined;

		const updateActiveFileFromScroll = () => {
			scrollFrameRef.current = null;

			const containerRect = scrollContainer.getBoundingClientRect();
			const targetOffset = 8;
			let nextFile = null;
			let nearestFile = null;
			let nearestDistance = Number.POSITIVE_INFINITY;

			for (const file of files) {
				const section = document.getElementById(fileElementId(file));
				if (!section) continue;

				const rect = section.getBoundingClientRect();
				const top = rect.top - containerRect.top;
				const bottom = rect.bottom - containerRect.top;

				if (top <= targetOffset && bottom > targetOffset) {
					nextFile = file;
				}

				const distance = Math.abs(top - targetOffset);
				if (distance < nearestDistance) {
					nearestFile = file;
					nearestDistance = distance;
				}
			}

			nextFile = nextFile || nearestFile || files[0];
			if (!nextFile || activeFileIdRef.current === nextFile.id) return;

			activeFileIdRef.current = nextFile.id;
			setActiveFileId(nextFile.id);

			if (lastWrittenFilePathRef.current !== nextFile.path) {
				writeActiveFilePathToHistory(nextFile.path);
				lastWrittenFilePathRef.current = nextFile.path;
			}
		};

		const handleScroll = () => {
			if (scrollFrameRef.current) return;
			scrollFrameRef.current = window.requestAnimationFrame(
				updateActiveFileFromScroll,
			);
		};

		scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
		scrollFrameRef.current = window.requestAnimationFrame(
			updateActiveFileFromScroll,
		);

		return () => {
			scrollContainer.removeEventListener("scroll", handleScroll);
			if (scrollFrameRef.current) {
				window.cancelAnimationFrame(scrollFrameRef.current);
				scrollFrameRef.current = null;
			}
		};
	}, [files]);

	useEffect(() => {
		if (!notice) return undefined;
		const timer = window.setTimeout(() => setNotice(null), 3500);
		return () => window.clearTimeout(timer);
	}, [notice]);

	const toggleDir = (path) => {
		setExpandedDirs((prev) => ({ ...prev, [path]: !(prev[path] ?? true) }));
	};

	const toggleFile = useCallback((fileId) => {
		setExpandedFiles((prev) => ({
			...prev,
			[fileId]: !(prev[fileId] ?? true),
		}));
	}, []);

	const setAllDirsExpanded = (expanded) => {
		setExpandedDirs(
			Object.fromEntries(dirPaths.map((path) => [path, expanded])),
		);
	};

	const toggleAllDirs = () => {
		setAllDirsExpanded(!allDirsExpanded);
	};

	const setValidMode = (nextMode) => {
		setMode(
			nextMode === VIEW_MODE.UNIFIED ? VIEW_MODE.UNIFIED : VIEW_MODE.SPLIT,
		);
	};

	const setValidSyntaxTheme = (nextTheme) => {
		const option = getDiffSyntaxThemeOption(nextTheme);
		setSyntaxThemePreference(option.value);
	};

	const togglePresentationMode = () => {
		setPresentationMode(
			resolvedPresentationMode === PRESENTATION_MODE.MODAL
				? PRESENTATION_MODE.FULLSCREEN
				: PRESENTATION_MODE.MODAL,
		);
	};

	return (
		<div
			data-diff-syntax-theme={resolvedSyntaxTheme.value}
			className={`fixed inset-0 z-[200] flex items-start justify-center text-[var(--color-text-primary)] transition-[background-color,opacity,padding] duration-200 ease-out ${
				isVisible ? "bg-black/35 opacity-100" : "bg-black/0 opacity-0"
			} ${isFullscreen ? "p-0" : "p-3 pt-[3vh] md:p-6 md:pt-[4vh]"}`}
			onMouseDown={(event) => {
				if (!isFullscreen && event.target === event.currentTarget) {
					closeViewer();
				}
			}}
		>
			<FileSwitcher
				open={fileSearchOpen}
				files={files}
				onSelectFile={selectFile}
				onClose={closeFileSearch}
			/>
			<FileTreeContextMenu
				menu={pathMenu}
				onCopyPath={copyPath}
				onCopyGitLabLink={copyGitLabPathLink}
				onClose={() => setPathMenu(null)}
			/>
			<DiffLineContextMenu
				menu={lineMenu}
				onCopyFormattedCode={copyFormattedLineSelection}
				onCopyGitLabLink={copyGitLabLineSelection}
				onClose={() => setLineMenu(null)}
			/>
			{notice && (
				<div className="pointer-events-none fixed left-1/2 top-4 z-[260] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2">
					<div
						className={`mx-auto flex w-fit max-w-full items-center gap-2 rounded-md border px-3 py-2 text-sm shadow-lg ${
							noticeIsError
								? "border-[var(--color-error-border)] bg-[var(--color-error-bg)] text-[var(--color-error-text)]"
								: "border-green-500/30 bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]"
						}`}
					>
						{noticeIsError ? (
							<AlertCircle className="h-4 w-4 shrink-0" />
						) : (
							<CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
						)}
						<span className="min-w-0 truncate">{notice}</span>
					</div>
				</div>
			)}
			<div
				className={`flex min-h-0 w-full flex-col overflow-hidden bg-[var(--color-bg-app)] shadow-2xl transition-[height,max-width,border-radius,border-color,opacity,transform] duration-200 ease-out ${
					isVisible
						? "translate-y-0 scale-100 opacity-100"
						: "translate-y-2 scale-[0.985] opacity-0"
				} ${
					isFullscreen
						? "h-full max-w-none rounded-none border border-transparent"
						: "h-[min(90vh,920px)] max-w-7xl rounded-xl border border-[var(--color-border)]"
				}`}
			>
				<header className="flex min-h-14 shrink-0 items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3">
					<div className="flex shrink-0 items-center gap-2">
						<button
							type="button"
							onClick={closeViewer}
							className="h-3.5 w-3.5 rounded-full border border-red-700/40 bg-red-500 transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-red-500/50"
							aria-label="Close diff viewer"
							title="Close"
						/>
						<button
							type="button"
							disabled
							className="h-3.5 w-3.5 cursor-not-allowed rounded-full border border-amber-900/50 bg-amber-700/60 opacity-60"
							aria-label="Minimize unavailable"
							title="Minimize unavailable"
						/>
						<button
							type="button"
							onClick={togglePresentationMode}
							className="h-3.5 w-3.5 rounded-full border border-green-700/40 bg-green-500 transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-green-500/50"
							aria-label={
								isFullscreen
									? "Show diff viewer as modal"
									: "Show diff viewer fullscreen"
							}
							title={isFullscreen ? "Show as modal" : "Enter fullscreen"}
						/>
					</div>
					<button
						type="button"
						onClick={() => setSidebarOpen((value) => !value)}
						className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
						aria-label={sidebarOpen ? "Hide file sidebar" : "Show file sidebar"}
					>
						{sidebarOpen ? (
							<PanelLeftClose className="h-4 w-4" />
						) : (
							<PanelLeftOpen className="h-4 w-4" />
						)}
					</button>
					<FileDiff className="h-5 w-5 shrink-0 text-[var(--color-accent)]" />
					<div className="min-w-0 flex-1">
						<div className="truncate text-sm font-semibold">
							!{mr?.iid}: {mr?.title}
						</div>
						<div className="truncate text-xs text-[var(--color-text-secondary)]">
							{formatProjectName(projectPath)} · {files.length} files ·{" "}
							<span className="text-green-600 dark:text-green-400">
								+{totalAdditions}
							</span>{" "}
							<span className="text-red-600 dark:text-red-400">
								-{totalDeletions}
							</span>
							{activeFile && (
								<>
									{" "}
									·{" "}
									{activeFileUrl ? (
										<a
											href={activeFileUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="text-[var(--color-link)] hover:underline"
										>
											{activeFile.path}
										</a>
									) : (
										activeFile.path
									)}
								</>
							)}
						</div>
					</div>
					<div className="inline-flex shrink-0 items-center gap-1">
						<Tooltip
							content={
								currentUserApproved ? "Already approved" : approvalTooltip
							}
							placement="bottom"
						>
							<button
								type="button"
								onClick={approveMR}
								disabled={approvalBusy || currentUserApproved}
								className={`inline-flex h-8 items-center gap-1.5 rounded border px-2 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-50 ${
									currentUserApproved
										? "border-green-500/35 bg-green-500/10 text-green-600 dark:text-green-400"
										: "border-green-500/35 text-green-600 hover:bg-green-500/10 dark:text-green-400"
								}`}
								aria-label="Approve merge request"
							>
								{approvalAction === "approve" ? (
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
								) : (
									<ThumbsUp className="h-3.5 w-3.5" />
								)}
								Approve
							</button>
						</Tooltip>
						<Tooltip
							content={
								currentUserApproved
									? "Remove your approval"
									: "Reject removes your approval after approving"
							}
							placement="bottom"
						>
							<button
								type="button"
								onClick={rejectMR}
								disabled={approvalBusy || !currentUserApproved}
								className="inline-flex h-8 items-center gap-1.5 rounded border border-red-500/35 px-2 text-xs font-medium text-red-500 transition-colors hover:bg-red-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50 disabled:cursor-not-allowed disabled:opacity-45"
								aria-label="Reject merge request"
							>
								{approvalAction === "reject" ? (
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
								) : (
									<ThumbsDown className="h-3.5 w-3.5" />
								)}
								Reject
							</button>
						</Tooltip>
					</div>
					<div className="inline-flex shrink-0 rounded-md border border-[var(--color-border)] p-0.5">
						<button
							type="button"
							onClick={() => setValidMode(VIEW_MODE.SPLIT)}
							className={`h-7 rounded px-2 text-xs font-medium ${
								mode === VIEW_MODE.SPLIT
									? "bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]"
									: "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
							}`}
						>
							Split
						</button>
						<button
							type="button"
							onClick={() => setValidMode(VIEW_MODE.UNIFIED)}
							className={`h-7 rounded px-2 text-xs font-medium ${
								mode === VIEW_MODE.UNIFIED
									? "bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]"
									: "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
							}`}
						>
							Unified
						</button>
					</div>
					<Tooltip
						content={`Syntax theme: ${
							selectedSyntaxTheme.value === DIFF_SYNTAX_THEME_AUTO
								? `Auto (${resolvedSyntaxTheme.label})`
								: selectedSyntaxTheme.label
						}`}
						placement="bottom"
					>
						<label className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-2 text-xs font-medium text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)] hover:text-[var(--color-text-primary)] focus-within:border-[var(--color-border-hover)] focus-within:ring-2 focus-within:ring-[var(--color-accent)]">
							<Palette className="h-3.5 w-3.5 shrink-0 opacity-70" />
							<span className="sr-only">Syntax color theme</span>
							<select
								value={selectedSyntaxTheme.value}
								onChange={(event) => setValidSyntaxTheme(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === "Escape") {
										event.stopPropagation();
									}
								}}
								className="max-w-36 cursor-pointer appearance-none bg-transparent text-xs font-medium text-inherit outline-none"
								aria-label="Syntax color theme"
							>
								{DIFF_SYNTAX_THEME_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
							<ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
						</label>
					</Tooltip>
					<Tooltip content="Search diff (⌘/Ctrl+F)" placement="bottom">
						<button
							type="button"
							onClick={openDiffSearch}
							className={`inline-flex h-8 w-8 items-center justify-center rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] ${
								diffSearchOpen
									? "bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]"
									: "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
							}`}
							aria-label="Search diff"
							aria-pressed={diffSearchOpen}
						>
							<Search className="h-4 w-4" />
						</button>
					</Tooltip>
					<Tooltip content="Search files (Ctrl+P)" placement="bottom">
						<button
							type="button"
							onClick={() => setFileSearchOpen(true)}
							className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
							aria-label="Search files"
						>
							<FileText className="h-4 w-4" />
						</button>
					</Tooltip>
					<div className="inline-flex shrink-0 rounded-md border border-[var(--color-border)] p-0.5">
						<Tooltip content="Previous file (Ctrl+Shift+Up)" placement="bottom">
							<button
								type="button"
								onClick={() => jumpToFile("previous")}
								disabled={files.length <= 1}
								className="inline-flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-45"
								aria-label="Previous file"
							>
								<FileUp className="h-4 w-4" />
							</button>
						</Tooltip>
						<Tooltip content="Next file (Ctrl+Shift+Down)" placement="bottom">
							<button
								type="button"
								onClick={() => jumpToFile("next")}
								disabled={files.length <= 1}
								className="inline-flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-45"
								aria-label="Next file"
							>
								<FileDown className="h-4 w-4" />
							</button>
						</Tooltip>
					</div>
					<Tooltip
						content={
							commentsVisible ? "Hide inline comments" : "Show inline comments"
						}
						placement="bottom"
					>
						<button
							type="button"
							onClick={() => setCommentsVisible((value) => !value)}
							className={`inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] ${
								commentsVisible
									? "bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]"
									: "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
							}`}
							aria-pressed={commentsVisible}
							aria-label={
								commentsVisible
									? "Hide inline comments"
									: "Show inline comments"
							}
						>
							{commentsLoading ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : commentsVisible ? (
								<Eye className="h-4 w-4" />
							) : (
								<EyeOff className="h-4 w-4" />
							)}
							<span className="tabular-nums">{commentCount}</span>
						</button>
					</Tooltip>
					<Tooltip
						content={
							diffCommentsExpanded
								? `Hide comments section (${diffCommentsShortcut})`
								: `Show comments section (${diffCommentsShortcut})`
						}
						placement="bottom"
					>
						<button
							type="button"
							onClick={toggleDiffCommentsSection}
							className={`inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] ${
								diffCommentsExpanded
									? "bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]"
									: "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
							}`}
							aria-pressed={diffCommentsExpanded}
							aria-label={
								diffCommentsExpanded
									? "Hide comments section"
									: "Show comments section"
							}
							aria-keyshortcuts={diffCommentsAriaShortcut}
						>
							{diffComments.loading ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<MessageSquare className="h-4 w-4" />
							)}
							<span className="tabular-nums">{diffCommentsCount}</span>
							<span className="hidden rounded border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-1 py-0.5 font-mono text-[10px] leading-none text-[var(--color-text-secondary)] lg:inline-flex">
								{diffCommentsShortcut}
							</span>
						</button>
					</Tooltip>
					<div className="inline-flex shrink-0 rounded-md border border-[var(--color-border)] p-0.5">
						<Tooltip content="Previous comment (Ctrl+Up)" placement="bottom">
							<button
								type="button"
								onClick={() => jumpToComment("previous")}
								disabled={commentsLoading || commentCount === 0}
								className="inline-flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-45"
								aria-label="Previous comment"
							>
								<ArrowUp className="h-4 w-4" />
							</button>
						</Tooltip>
						<Tooltip content="Next comment (Ctrl+Down)" placement="bottom">
							<button
								type="button"
								onClick={() => jumpToComment("next")}
								disabled={commentsLoading || commentCount === 0}
								className="inline-flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-45"
								aria-label="Next comment"
							>
								<ArrowDown className="h-4 w-4" />
							</button>
						</Tooltip>
					</div>
					<Tooltip content="Toggle line wrapping" placement="bottom">
						<button
							type="button"
							onClick={() => setWrap((value) => !value)}
							className={`inline-flex h-8 w-8 items-center justify-center rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] ${
								wrap
									? "bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]"
									: "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
							}`}
							aria-pressed={wrap}
							aria-label="Toggle line wrapping"
						>
							<WrapText className="h-4 w-4" />
						</button>
					</Tooltip>
					<Tooltip content="Reload diff" placement="bottom">
						<button
							type="button"
							onClick={reloadDiffAndComments}
							disabled={loading}
							className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-60"
							aria-label="Reload diff"
						>
							<RefreshCcw
								className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
							/>
						</button>
					</Tooltip>
					{safeMrUrl && (
						<>
							<Tooltip
								content={
									mrLinkCopied ? "Copied" : "Copy merge request GitLab link"
								}
								placement="bottom"
							>
								<button
									type="button"
									onClick={copyMergeRequestLink}
									className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-[background-color,color,transform] duration-150 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] ${
										mrLinkCopied
											? "scale-105 bg-green-500/10 text-green-600 dark:text-green-400"
											: "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
									}`}
									aria-label="Copy merge request GitLab link"
								>
									{mrLinkCopied ? (
										<CheckCircle2 className="h-4 w-4" />
									) : (
										<Copy className="h-4 w-4" />
									)}
								</button>
							</Tooltip>
							<Tooltip
								content="Open merge request in GitLab"
								placement="bottom"
							>
								<a
									href={safeMrUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
									aria-label="Open merge request in GitLab"
								>
									<ExternalLink className="h-4 w-4" />
								</a>
							</Tooltip>
						</>
					)}
				</header>
				{diffSearchOpen && (
					<DiffTextSearchBar
						query={diffSearchQuery}
						onQueryChange={updateDiffSearchQuery}
						caseInsensitive={diffSearchCaseInsensitive}
						onCaseInsensitiveChange={updateDiffSearchCaseInsensitive}
						resultCount={diffSearchResults.length}
						activeIndex={diffSearchDisplayIndex}
						activeResult={activeDiffSearchResult}
						onPrevious={() => jumpToDiffSearchMatch("previous")}
						onNext={() => jumpToDiffSearchMatch("next")}
						onClose={closeDiffSearch}
						inputRef={diffSearchInputRef}
					/>
				)}

				<div className="flex min-h-0 flex-1">
					{sidebarOpen && (
						<aside className="diff-fast-motion hidden w-72 shrink-0 overflow-hidden border-r border-[var(--color-border)] bg-[var(--color-bg-primary)] md:flex md:flex-col">
							<div className="sticky top-0 z-20 shrink-0 space-y-2 border-b border-[var(--color-border)] bg-[var(--color-bg-primary)] px-2 py-2">
								<div className="flex items-center gap-1.5">
									<div className="min-w-0 flex-1 truncate text-xs font-semibold text-[var(--color-text-secondary)]">
										Files changed
										<span className="ml-1 font-mono text-[10px] text-[var(--color-text-tertiary)]">
											{sidebarFilter
												? `${filteredFileCount}/${files.length}`
												: files.length}
										</span>
									</div>
									<Tooltip content={toggleAllDirsLabel} placement="bottom">
										<button
											type="button"
											onClick={toggleAllDirs}
											disabled={dirPaths.length === 0}
											className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-45"
											aria-label={toggleAllDirsLabel}
										>
											{allDirsExpanded ? (
												<ChevronsUp className="h-4 w-4" />
											) : (
												<ChevronsDown className="h-4 w-4" />
											)}
										</button>
									</Tooltip>
								</div>
								<div className="diff-search-shell flex h-8 items-center gap-1.5 rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-2 transition-[border-color,box-shadow]">
									<Search className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-tertiary)]" />
									<input
										type="text"
										value={sidebarFilter}
										onChange={(event) => setSidebarFilter(event.target.value)}
										placeholder="Filter files"
										className="diff-search-input h-full min-w-0 flex-1 bg-transparent text-xs text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)]"
									/>
									{sidebarFilter && (
										<button
											type="button"
											onClick={() => setSidebarFilter("")}
											className="no-hover-lift inline-flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-primary)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
											aria-label="Clear file filter"
										>
											<X className="h-3.5 w-3.5" />
										</button>
									)}
								</div>
							</div>
							<div
								ref={sidebarListRef}
								className="min-h-0 flex-1 overflow-y-auto p-2"
							>
								{filteredFileCount > 0 ? (
									filteredFileTree.children.map((node) => (
										<FileTreeNode
											key={node.path}
											node={node}
											expandedDirs={expandedDirs}
											onToggleDir={toggleDir}
											onSelectFile={selectFile}
											onContextMenu={openPathMenu}
											activeFileId={activeFileId}
											forceExpanded={Boolean(sidebarFilter.trim())}
										/>
									))
								) : (
									<div className="px-2 py-3 text-xs text-[var(--color-text-secondary)]">
										{files.length > 0 ? "No matching files" : "No files loaded"}
									</div>
								)}
							</div>
						</aside>
					)}

					<div className="relative min-w-0 flex-1 overflow-hidden">
						<main
							ref={mainRef}
							className="h-full min-w-0 overflow-y-auto px-3 pt-0"
							style={{
								paddingBottom: diffCommentsExpanded
									? commentsPanelHeight + 12
									: 64,
							}}
						>
							{loading && !loaded ? (
								<div className="flex h-full items-center justify-center">
									<div className="text-center text-[var(--color-text-secondary)]">
										<Loader2 className="mx-auto mb-3 h-10 w-10 animate-spin" />
										<div className="text-sm">Loading diff...</div>
									</div>
								</div>
							) : error ? (
								<div className="rounded-lg border border-[var(--color-error-border)] bg-[var(--color-error-bg)] p-4 text-[var(--color-error-text)]">
									<div className="flex items-center gap-2 font-medium">
										<AlertCircle className="h-5 w-5" />
										Unable to load diff
									</div>
									<p className="mt-2 text-sm">{error}</p>
									<button
										type="button"
										onClick={reload}
										className="mt-3 rounded-md border border-[var(--color-error-border)] px-3 py-1.5 text-xs font-medium hover:bg-red-500/10 focus:outline-none focus:ring-2 focus:ring-red-500/50"
									>
										Retry
									</button>
								</div>
							) : files.length === 0 ? (
								<div className="rounded bg-[var(--color-info-bg)] px-4 py-3 text-[var(--color-info-text)]">
									No file changes found for this merge request.
								</div>
							) : (
								<div className="space-y-4 pb-4">
									{!canComment && (
										<div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-2 text-xs text-[var(--color-text-secondary)]">
											Diff refs were not available from GitLab, so inline code
											comments are disabled for this load.
										</div>
									)}
									{commentsError && (
										<div className="flex items-center gap-2 rounded-md border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-3 py-2 text-xs text-[var(--color-error-text)]">
											<AlertCircle className="h-3.5 w-3.5 shrink-0" />
											<span className="min-w-0 flex-1 truncate">
												{commentsError}
											</span>
											<button
												type="button"
												onClick={() => reloadComments({ force: true })}
												className="shrink-0 rounded border border-[var(--color-error-border)] px-2 py-1 font-medium hover:bg-red-500/10 focus:outline-none focus:ring-2 focus:ring-red-500/50"
											>
												Retry
											</button>
										</div>
									)}
									{files.map((file, index) => (
										<DiffFileSection
											key={file.id}
											file={file}
											expanded={expandedFiles[file.id] ?? true}
											onToggle={toggleFile}
											mode={mode}
											wrap={wrap}
											diffTheme={diffTheme}
											discussions={discussions}
											commentsVisible={commentsVisible}
											canComment={canComment}
											createCodeComment={createCodeComment}
											updateCodeComment={updateCodeComment}
											deleteCodeComment={deleteCodeComment}
											replyCodeComment={replyCodeComment}
											addCodeCommentReaction={addCodeCommentReaction}
											reactingEmojiKey={reactingEmojiKey}
											onCommentCreated={setNotice}
											fullFileUrl={buildGitLabFileUrl({
												projectPath,
												diffRefs,
												file,
											})}
											onOpenLineMenu={openLineMenu}
											onSelectLine={selectDiffLine}
											selectedRange={selectedRange}
											scrollRootRef={mainRef}
											loadFileContent={loadFileContent}
											eagerRender={index < DIFF_EAGER_FILE_COUNT}
										/>
									))}
								</div>
							)}
						</main>
						<DiffCommentsSection
							mr={mr}
							comments={diffComments}
							height={commentsPanelHeight}
							onResizeStart={startCommentsResize}
							renderDiscussionReference={renderDiscussionReference}
							shortcut={diffCommentsShortcut}
							ariaShortcut={diffCommentsAriaShortcut}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MRDiffViewer;
